import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { createPortal } from "react-dom";
import {
  Phone, Search, Loader2, Play, Pause, RotateCcw, Volume2, VolumeX, ChevronDown,
  Share2, Check, ChevronLeft, MoreVertical, MessageSquare,
  Radio, Sparkles, Info, Plus, ChevronUp, ChevronDown as ChevronDownIcon,
  Inbox, Star, Scissors, Trash2, X, Archive, ShieldCheck, Eye, LogOut,
} from "lucide-react";

// Public viewer URL (winsboard) — used for clip share links.
const PUBLIC_BOARD_URL = "https://winsboard.felloinnovate.ai";

import { useAuth, buildNavUrl, EMBED_KEY } from "@/hooks/useAuth";
import { PageTabs } from "@/components/PageTabs";
import { MaskedContactName } from "@/components/ui/MaskedContactName";
import { maskContactName } from "@/lib/utils";

function isSMS(call: { channel?: string | null }) {
  return (call.channel || "").startsWith("SMS");
}

// ── Types ───────────────────────────────────────────────────────────────────
interface TransferAttempt {
  status?: string;
  transferToPhone?: string;
  completedAt?: string;
  failureReason?: string;
}

// How a shared clip treats the names of people on the call.
//   off       — real names in the transcript + original audio
//   anonymize — generic labels in the transcript + a beep over spoken names
//   silence   — generic labels in the transcript + muted audio over spoken names
export type RedactionMode = "off" | "anonymize" | "silence";

export interface Clip {
  id: string;
  call_id: string;
  contact_name: string | null;
  business_name: string | null;
  start_sec: number;
  end_sec: number;
  title: string | null;
  note: string | null;
  result: string | null;
  transcript_excerpt: string | null;
  redaction_mode?: RedactionMode;
  created_by_email?: string | null;
  created_by_name?: string | null;
  created_at: string;
  updated_at: string;
}

const REDACTION_OPTIONS: { value: RedactionMode; label: string; hint: string; icon: typeof Eye }[] = [
  { value: "off", label: "Show names", hint: "Real names + original audio", icon: Eye },
  { value: "anonymize", label: "Anonymize", hint: "Hide names in text, beep them in audio", icon: ShieldCheck },
  { value: "silence", label: "Silence", hint: "Hide names in text, mute them in audio", icon: VolumeX },
];

// Segmented 3-way control for picking a clip's redaction mode. Used both at
// create time and to change a saved clip's mode retrospectively. Exported so the
// public single-clip share page can offer the same control to clip creators.
export function RedactionModeSelect({
  value, onChange, disabled,
}: {
  value: RedactionMode;
  onChange: (m: RedactionMode) => void;
  disabled?: boolean;
}) {
  const active = REDACTION_OPTIONS.find((o) => o.value === value) ?? REDACTION_OPTIONS[1];
  return (
    <div>
      <div className="grid grid-cols-3 gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1">
        {REDACTION_OPTIONS.map((o) => {
          const Icon = o.icon;
          const on = o.value === value;
          return (
            <button
              key={o.value}
              type="button"
              disabled={disabled}
              onClick={() => onChange(o.value)}
              className={
                "flex items-center justify-center gap-1.5 h-8 rounded-md text-[12px] font-medium transition disabled:opacity-50 " +
                (on ? "bg-white text-[#F5735A] shadow-sm border border-slate-200" : "text-slate-600 hover:text-slate-900")
              }
            >
              <Icon className="w-3.5 h-3.5" /> {o.label}
            </button>
          );
        })}
      </div>
      <div className="mt-1 text-[11px] text-slate-500">{active.hint}</div>
    </div>
  );
}

interface CallLog {
  id: string;
  business_name: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  contact_intent: string | null;
  duration: number | null;
  total_turns: number | null;
  call_type: string | null;
  call_summary: string | null;
  transfer_attempts: TransferAttempt[] | null;
  property_json: Record<string, unknown> | null;
  current_objective_name: string | null;
  enrollment_reason: string | null;
  recording_url: string | null;
  created_at: string;
  contact_metadata?: {
    property_address?: string;
    state?: string;
    num_properties?: number;
    mls_opportunity?: string;
    contact_created_at?: string;
  } | null;
  // LLM-generated contact tags stored on the row — preferred over regex computation.
  contact_tags?: ContactTag[] | null;
  // Channel: SMS_OUTBOUND, SMS_INBOUND, etc. — null/absent = voice call.
  channel?: string | null;
  // HiFello system tags (includes zillow_preferred flag, etc.)
  tags?: Record<string, unknown> | null;
  // Set by the server for the pinned agent-side handoff call(s).
  agent_side?: boolean;
}

// ── Contact tags — deterministic tagger from metadata + call summaries ─────

interface ContactTag {
  label: string;
  color: string;
  textColor: string;
  reason: string; // why this contact qualified
}

const SOUTH_STATES = new Set(["FL", "GA", "SC", "NC", "AL", "MS", "LA", "TX", "TN", "AR", "VA", "KY", "WV"]);
const NORTHEAST_STATES = new Set(["NY", "NJ", "CT", "MA", "PA", "MD", "DE", "RI", "NH", "VT", "ME"]);
const CALI_STATES = new Set(["CA"]);
const MIDWEST_STATES = new Set(["OH", "MI", "IN", "IL", "WI", "MN", "IA", "MO", "ND", "SD", "NE", "KS"]);
const VACATION_STATES = new Set(["FL", "HI", "UT", "CO"]);
const VACATION_CITIES = ["park city", "palm beach", "boca raton", "naples", "miami beach", "key west", "aspen", "vail", "telluride", "scottsdale", "maui", "saint pete", "palmetto", "clearwater", "sarasota", "destin", "fort lauderdale"];

// Keyword matchers — return the matched phrase or null
function findMatch(text: string, re: RegExp): string | null {
  const m = text.match(re);
  return m ? m[0] : null;
}

function computeContactTags(calls: CallLog[]): ContactTag[] {
  // Check if any call in the group has zillow_preferred flag
  const hasZillowPreferred = calls.some(c => c.tags?.zillow_preferred === true);
  const zillowTag: ContactTag = { label: "Zillow Preferred", color: "bg-[#0C4599]", textColor: "text-white", reason: "Zillow preferred lead" };

  // Prefer stored LLM-generated tags if any call in the group has them
  const storedTags = calls.find(c => c.contact_tags && c.contact_tags.length > 0)?.contact_tags;
  if (storedTags) {
    if (hasZillowPreferred && !storedTags.some(t => t.label === "Zillow Preferred")) {
      return [zillowTag, ...storedTags];
    }
    return storedTags;
  }

  // Fallback: regex-based computation (pre-migration or untagged calls)
  const tags: ContactTag[] = [];
  const meta = calls.find(c => c.contact_metadata)?.contact_metadata;
  const allSummaries = calls.map(c => (c.call_summary || "").toLowerCase()).join(" ");
  const state = meta?.state?.trim() || "";
  const mls = (meta?.mls_opportunity || "").toLowerCase();
  const address = (meta?.property_address || "").toLowerCase();
  const contactCreated = meta?.contact_created_at;

  // Check expired first — if expired, skip Old/New (irrelevant)
  const hasExpiredMls = mls.includes("✓") || mls.includes("\u2713");
  const expiredMatch = findMatch(allSummaries, /\b(expired|old listing|remove.*listing|was listed|back on.*(the )?market|put it back|previously listed|under contract|contract signed|midst of selling|in the middle of selling|already.*selling|sitting on it)\b/i);
  const isExpired = hasExpiredMls || !!expiredMatch;

  // Old vs New contact — skip if expired
  if (contactCreated && !isExpired) {
    const created = new Date(contactCreated);
    const ageMs = Date.now() - created.getTime();
    const ageYears = ageMs / (365.25 * 24 * 60 * 60 * 1000);
    const dateStr = created.toLocaleDateString("en-US", { month: "short", year: "numeric" });
    if (ageYears > 1) {
      tags.push({ label: "Old Contact", color: "bg-[#F1F4F7]", textColor: "text-[#6B748E]", reason: `Contact created ${dateStr} (${Math.floor(ageYears)}+ years ago)` });
    } else {
      const ageMonths = Math.floor(ageMs / (30.44 * 24 * 60 * 60 * 1000));
      tags.push({ label: "New Contact", color: "bg-[#F0F7FF]", textColor: "text-[#3D93F5]", reason: `Contact created ${dateStr} (${ageMonths} months ago)` });
    }
  }

  // Seller — require strong selling intent, not just passing mentions
  const sellerMls = /\bs\d/i.test(mls); // "S1", "S3" etc — confirmed seller listings in MLS
  const sellerKeyword = findMatch(allSummaries, /\b(wants? to sell|interested in selling|open to selling|planning to sell|considering selling|ready to list|wants? to list|looking to sell|intends? to sell|liquidate|downsize|downsizing|sell.{0,30}property|sell.{0,30}home|sell.{0,30}house|goal is to sell|plan.{0,10}to sell)\b/i);
  if (sellerMls || sellerKeyword) {
    const reason = sellerMls
      ? `MLS shows ${mls.match(/s\d+/i)?.[0]?.toUpperCase()} seller listing(s)`
      : `Transcript: "${sellerKeyword}"`;
    tags.push({ label: "Seller", color: "bg-[#E3FCEF]", textColor: "text-[#31A172]", reason });
  }

  // Buyer — require active buying intent
  const buyerMls = /\bb\d/i.test(mls);
  const buyerKeyword = findMatch(allSummaries, /\b(looking to buy|wants? to buy|first.time buyer|home search|house hunt|exploring.{0,15}homes|looking for.{0,50}(home|house|condo|property|ranch|bungalow|townhome)|planning to (buy|purchase)|interested in buying|actively looking)\b/i);
  if (buyerMls || buyerKeyword) {
    const reason = buyerMls
      ? `MLS shows ${mls.match(/b\d+/i)?.[0]?.toUpperCase()} buyer transaction(s)`
      : `Transcript: "${buyerKeyword}"`;
    tags.push({ label: "Buyer", color: "bg-[#F4F3FF]", textColor: "text-[#8D6AE7]", reason });
  }

  // Luxury — price ≥ $1M mentioned in context of property value
  const priceMatches = allSummaries.match(/\$[\d,]+(?:,\d{3})*(?:\.\d+)?/g) || [];
  const luxuryPrice = priceMatches.find(p => parseFloat(p.replace(/[$,]/g, "")) >= 1_000_000);
  if (luxuryPrice) {
    tags.push({ label: "Luxury", color: "bg-[#FFFAE6]", textColor: "text-[#F08400]", reason: `Price point ${luxuryPrice} mentioned in call` });
  }

  // Cash offer — "cash buyer", "cash offer", "cash, firm" — not just any mention of "cash"
  const cashMatch = findMatch(allSummaries, /\b(cash (buyer|offer|sale|deal|close)|pay.{0,5}cash|cash.{0,3}firm|cash.{0,10}no.{0,5}(mortgage|financing|contingenc))\b/i);
  if (cashMatch) {
    tags.push({ label: "Cash Offer", color: "bg-[#E7FDFD]", textColor: "text-[#098486]", reason: `Transcript: "${cashMatch}"` });
  }

  // Expired / Previously listed / Under contract (already computed above)
  if (isExpired) {
    const reason = hasExpiredMls
      ? `MLS shows completed/expired listing (${mls.match(/s\d+\s*✓/i)?.[0] || "✓"})`
      : `Transcript: "${expiredMatch}"`;
    tags.push({ label: "Expired/Sold", color: "bg-[#FFEBE6]", textColor: "text-[#F02C00]", reason });
  }

  // Geography
  if (SOUTH_STATES.has(state)) tags.push({ label: "South", color: "bg-[#FDF1EF]", textColor: "text-[#FF725C]", reason: `Property in ${state} (Southern US)` });
  else if (CALI_STATES.has(state)) tags.push({ label: "California", color: "bg-[#FDF1EF]", textColor: "text-[#FF725C]", reason: `Property in California` });
  else if (NORTHEAST_STATES.has(state)) tags.push({ label: "Northeast", color: "bg-[#FDF1EF]", textColor: "text-[#FF725C]", reason: `Property in ${state} (Northeast US)` });
  else if (MIDWEST_STATES.has(state)) tags.push({ label: "Midwest", color: "bg-[#FDF1EF]", textColor: "text-[#FF725C]", reason: `Property in ${state} (Midwest US)` });

  // Vacation market
  const vacCity = VACATION_CITIES.find(c => address.includes(c));
  if (VACATION_STATES.has(state) && vacCity) {
    tags.push({ label: "Vacation Market", color: "bg-[#FFCAD4]", textColor: "text-[#B4289A]", reason: `Property in ${vacCity} — known vacation/resort area` });
  }

  if (hasZillowPreferred) tags.unshift(zillowTag);
  return tags;
}

function TagChip({ tag }: { tag: ContactTag }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [tip, setTip] = useState<{ x: number; y: number } | null>(null);
  return (
    <span
      ref={ref}
      onMouseEnter={() => {
        const r = ref.current?.getBoundingClientRect();
        if (r) setTip({ x: r.left + r.width / 2, y: r.top });
      }}
      onMouseLeave={() => setTip(null)}
      className={`inline-flex items-center px-2 py-0.5 rounded-[var(--radius-3)] text-[10px] font-semibold cursor-help ${tag.color} ${tag.textColor}`}
    >
      {tag.label}
      {tip && createPortal(
        <span
          style={{ left: tip.x, top: tip.y }}
          className="fixed -translate-x-1/2 -translate-y-full -mt-1.5 px-2.5 py-1.5 rounded-md bg-slate-800 text-white text-[11px] font-normal leading-snug max-w-[280px] z-[9999] shadow-lg"
        >
          {tag.reason}
        </span>,
        document.body,
      )}
    </span>
  );
}

function ContactTagChips({ tags }: { tags: ContactTag[] }) {
  if (tags.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {tags.map((t) => <TagChip key={t.label} tag={t} />)}
    </div>
  );
}

interface CallMessage {
  message_index: number;
  role: "user" | "assistant" | "tool" | string;
  content: string | null;
  item_name: string | null;
  item_output: string | null;
  // Some message rows may carry per-message timestamps. We don't hard-code
  // the column name because the schema may vary — we sniff for any of these
  // at render time so the transcript can sync to the real audio offsets.
  [k: string]: unknown;
}

interface AudioWord {
  [k: string]: unknown;
}

const CHROME_BG = "#353E5A";
const RAIL_BG = "#2C3349";

// ── Root ────────────────────────────────────────────────────────────────────
export default function CallsApp() {
  const { user, isLoading, isAuthenticated } = useAuth();
  if (isLoading) {
    return (
      <div className="min-h-screen grid place-items-center bg-white">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen grid place-items-center bg-gradient-to-br from-[#fff7f3] via-[#fefbf9] to-[#f3f6ff] p-6">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl border border-black/5 p-10 text-center">
          <div className="w-14 h-14 mx-auto mb-5 rounded-2xl bg-[#F5735A] text-white grid place-items-center">
            <Phone className="w-7 h-7" />
          </div>
          <h1 className="font-serif text-3xl mb-2">Felix Calls</h1>
          <p className="text-slate-500 mb-8">
            Sign in with your <span className="font-medium text-slate-900">@fello.ai</span> account to browse Felix call recordings and transcripts.
          </p>
          <a href="/auth/google" {...(EMBED_KEY ? { target: "_top" } : {})} className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#F5735A] text-white font-medium hover:opacity-90 transition">
            Sign in with Google
          </a>
        </div>
      </div>
    );
  }
  return <CallsLayout userEmail={user?.email ?? ""} />;
}

// ── Layout (4 vertical zones: rail · list · center · contact-details) ──────
type Mode = "handoff" | "search" | "clips";

function CallsLayout({ userEmail }: { userEmail: string }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<Mode>("handoff");

  // Handoff list — server already merges approved-overrides in and filters
  // archived out, so this is the live "confirmed" set.
  const { data: calls = [], isLoading, error } = useQuery({
    queryKey: ["calls"],
    queryFn: async (): Promise<CallLog[]> => {
      const res = await fetch("/api/calls");
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    retry: 1,
  });

  // Override sets — drive the per-call action buttons (in/out of Handoff).
  const { data: overrides } = useQuery({
    queryKey: ["handoff-overrides"],
    queryFn: async (): Promise<{ approved: string[]; archived: string[] }> => {
      const res = await fetch("/api/handoff/overrides");
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
  });
  const archivedSet = useMemo(() => new Set(overrides?.archived ?? []), [overrides]);
  const handoffIds = useMemo(() => new Set(calls.map((c) => c.id)), [calls]);

  // Search — by id / name / phone / email. Drives its own results query.
  const [searchQ, setSearchQ] = useState("");
  const { data: searchResults = [], isFetching: searching } = useQuery({
    queryKey: ["calls-search", searchQ.trim()],
    queryFn: async (): Promise<CallLog[]> => {
      const res = await fetch(`/api/calls/search?q=${encodeURIComponent(searchQ.trim())}`);
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    enabled: mode === "search" && searchQ.trim().length >= 2,
  });

  const setHandoffState = useMutation({
    mutationFn: async (vars: { call_id: string; status: "approved" | "archived" }) => {
      const res = await fetch("/api/handoff/state", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(vars),
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["calls"] });
      qc.invalidateQueries({ queryKey: ["handoff-overrides"] });
    },
  });

  // The list backing the current mode + its loading state. Clips mode has no
  // call list — it shows clips instead, so no call is selected there.
  const activeList = mode === "handoff" ? calls : mode === "search" ? searchResults : EMPTY_CALLS;
  const activeLoading = mode === "handoff" ? isLoading : searching;

  // "Open full conversation" from a clip: switch to the list that holds the
  // call (the conversations list, or Search by id if it isn't in it) and
  // select it once the mode-change reset below has run.
  const pendingCallRef = useRef<string | null>(null);
  const [selectedClipId, setSelectedClipId] = useState<string | null>(null);
  const openCallFromClip = useCallback((callId: string) => {
    pendingCallRef.current = callId;
    if (handoffIds.has(callId)) {
      setMode("handoff");
    } else {
      setSearchQ(callId);
      setMode("search");
    }
  }, [handoffIds]);

  const initialId = useMemo(
    () => new URLSearchParams(window.location.search).get("call"),
    [],
  );
  const [selectedId, setSelectedId] = useState<string | null>(initialId);
  // "journey" = show the contact journey; "call" = show individual call details.
  const [viewMode, setViewMode] = useState<"journey" | "call">("call");
  // Reset selection when switching tabs so we always land on the new list.
  useEffect(() => {
    setSelectedId(pendingCallRef.current);
    pendingCallRef.current = null;
    setViewMode("call");
  }, [mode]);
  useEffect(() => {
    if (!selectedId && activeList.length > 0) setSelectedId(activeList[0].id);
  }, [activeList, selectedId]);

  const selected = useMemo(
    () => activeList.find((c) => c.id === selectedId) ?? null,
    [activeList, selectedId],
  );

  // All calls from the same contact as the selected call (for the Journey tab).
  const siblingCalls = useMemo(() => {
    if (!selected) return [];
    const key = contactKey(selected);
    return activeList
      .filter((c) => contactKey(c) === key)
      .sort((a, b) => +new Date(a.created_at) - +new Date(b.created_at)); // oldest first
  }, [selected, activeList]);

  // URL ↔ selectedId — replaceState so each pick doesn't pile up history.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (selectedId && params.get("call") !== selectedId) {
      params.set("call", selectedId);
      window.history.replaceState({}, "", `${window.location.pathname}?${params.toString()}`);
    }
  }, [selectedId]);

  // Favorites — global shared (server-backed), with an optimistic toggle.
  const { data: favData } = useQuery({
    queryKey: ["call-favorites"],
    queryFn: async (): Promise<{ call_ids: string[] }> => {
      const res = await fetch("/api/handoff/favorites");
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
  });
  const favorites = useMemo(() => new Set(favData?.call_ids ?? []), [favData]);
  const favoriteMutation = useMutation({
    mutationFn: async (vars: { call_id: string; favorite: boolean }) => {
      const res = await fetch("/api/handoff/favorite", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(vars),
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: ["call-favorites"] });
      const prev = qc.getQueryData<{ call_ids: string[] }>(["call-favorites"]);
      const set = new Set(prev?.call_ids ?? []);
      if (vars.favorite) set.add(vars.call_id); else set.delete(vars.call_id);
      qc.setQueryData(["call-favorites"], { call_ids: [...set] });
      return { prev };
    },
    onError: (_e, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(["call-favorites"], ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["call-favorites"] }),
  });
  const toggleFavorite = useCallback((id: string) => {
    favoriteMutation.mutate({ call_id: id, favorite: !favorites.has(id) });
  }, [favoriteMutation, favorites]);

  // Per-mode action shown in the call detail header.
  const headerAction = selected ? renderHeaderAction({
    call: selected, mode,
    inHandoff: handoffIds.has(selected.id),
    pending: setHandoffState.isPending,
    setState: (status) => setHandoffState.mutate({ call_id: selected.id, status }),
  }) : null;

  const emptyLabel = activeLoading ? "Loading…"
    : mode === "search"
      ? (searchQ.trim().length < 2 ? "Search to find a call" : "No matches")
      : (activeList.length === 0 ? "No calls yet" : "Select a call");

  return (
    <div className="h-screen w-full flex flex-col bg-white">
      <FelixCallsHeader userEmail={userEmail} />
      <div className="flex-1 flex min-h-0 gap-2 pr-2 pb-2" style={{ background: CHROME_BG }}>
        <SideRail mode={mode} onMode={setMode} />
        <div className="w-[320px] flex-shrink-0 min-h-0 bg-white rounded-lg overflow-hidden">
          {mode === "clips" ? (
            <ClipsListPanel selectedId={selectedClipId} onSelect={setSelectedClipId} />
          ) : mode === "search" ? (
            <SearchPanel
              query={searchQ}
              onQuery={setSearchQ}
              results={searchResults}
              loading={searching}
              selectedId={selectedId}
              onSelect={setSelectedId}
              inHandoff={handoffIds}
              archivedSet={archivedSet}
            />
          ) : (
            <HandoffListPanel
              title="Felix conversations"
              icon={<Inbox className="w-4 h-4 text-slate-500" />}
              showChips={mode === "handoff"}
              calls={activeList}
              loading={activeLoading}
              error={error as Error | null}
              selectedId={selectedId}
              onSelect={(id) => { setSelectedId(id); setViewMode("call"); }}
              onSelectGroup={(id) => { setSelectedId(id); setViewMode("journey"); }}
              favorites={favorites}
              onToggleFavorite={toggleFavorite}
            />
          )}
        </div>
        <main className="flex-1 min-w-0 min-h-0 bg-white rounded-lg overflow-hidden">
          {mode === "clips" ? (
            <ClipsMainView clipId={selectedClipId} onOpenCall={openCallFromClip} />
          ) : selected && viewMode === "journey" && siblingCalls.length > 1 ? (
            <div className="h-full flex flex-col">
              {/* Journey header */}
              <div className="flex-shrink-0 px-6 py-4 border-b border-slate-200">
                <h2 className="text-lg font-semibold text-slate-900">
                  Felix Journey with <MaskedContactName name={selected.contact_name} />
                </h2>
                {selected.business_name && <p className="text-sm text-slate-500 mt-0.5">{selected.business_name}</p>}
                <p className="text-xs text-slate-400 mt-1">
                  {siblingCalls.length} interactions · {new Date(siblingCalls[0].created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })} – {new Date(siblingCalls[siblingCalls.length - 1].created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </p>
                <div className="mt-2">
                  <ContactTagChips tags={computeContactTags(siblingCalls)} />
                </div>
              </div>
              {/* Journey timeline */}
              <div className="flex-1 overflow-y-auto px-6 py-5">
                <ContactJourney
                  calls={siblingCalls}
                  currentCallId={selected.id}
                  onSelectCall={(id) => { setSelectedId(id); setViewMode("call"); }}
                />
              </div>
            </div>
          ) : selected ? (
            <CallDetailsView
              call={selected}
              headerAction={headerAction}
              onBackToJourney={siblingCalls.length > 1 ? () => setViewMode("journey") : undefined}
            />
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-slate-400 gap-2">
              {mode === "search" ? <Search className="w-8 h-8 opacity-40" /> : <Phone className="w-8 h-8 opacity-40" />}
              <p className="text-sm">{emptyLabel}</p>
            </div>
          )}
        </main>
        {selected && (
          <div className="w-[320px] flex-shrink-0 min-h-0 bg-white rounded-lg overflow-hidden">
            <ContactDetailsPanel call={selected} siblingCalls={siblingCalls} />
          </div>
        )}
      </div>
    </div>
  );
}

// The header button for a selected call depends on which tab you're in and
// Header action for the selected call — approve to handoff from search mode.
function renderHeaderAction({
  mode, inHandoff, pending, setState,
}: {
  call: CallLog;
  mode: Mode;
  inHandoff: boolean;
  pending: boolean;
  setState: (status: "approved") => void;
}): React.ReactNode {
  if (mode === "handoff") {
    return null;
  }
  // Search mode.
  if (inHandoff) {
    return (
      <span className="h-9 px-3 rounded-lg inline-flex items-center gap-1.5 text-[13px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
        <Check className="w-4 h-4" /> In Felix conversations
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={() => setState("approved")}
      disabled={pending}
      className="h-9 px-3.5 rounded-lg inline-flex items-center gap-1.5 text-[13px] font-medium bg-[#F5735A] text-white hover:opacity-90 shadow-sm disabled:opacity-50"
      title="Add this call to the Felix conversations list"
    >
      {pending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
      Add to Felix conversations
    </button>
  );
}

// ── Top header ──────────────────────────────────────────────────────────────
function FelixCallsHeader({ userEmail }: { userEmail: string }) {
  const initials = (userEmail[0] ?? "U").toUpperCase();
  return (
    <header
      className="h-[41px] flex-shrink-0 flex items-center justify-between"
      style={{ background: CHROME_BG }}
    >
      <div className="flex items-center flex-1 min-w-0">
        <a href={buildNavUrl({})} title="Back to Felix Wins" className="w-14 h-[41px] flex items-center justify-center">
          <div className="w-7 h-7 rounded-full grid place-items-center bg-[#F5735A] text-white">
            <Phone className="w-3.5 h-3.5" />
          </div>
        </a>
        {EMBED_KEY ? (
          <PageTabs active="calls" dark />
        ) : (
          <a href={buildNavUrl({})} className="text-[12px] text-white/70 hover:text-white inline-flex items-center gap-1">
            <ChevronLeft className="w-3 h-3" /> Felix Wins
          </a>
        )}
        <div className="flex-1" />
      </div>
      <div className="pr-2">
        <a
          href="/auth/logout"
          title="Sign out"
          className="flex items-center gap-1.5 rounded-full ring-2 ring-white/10 hover:ring-white/30 transition pl-1 pr-2 py-0.5"
        >
          <div className="w-7 h-7 rounded-full grid place-items-center text-[10px] font-medium bg-[#14BACB] text-white">
            {initials}
          </div>
          <LogOut size={12} className="text-white/60" />
        </a>
      </div>
    </header>
  );
}

// ── Narrow icon rail — Felix conversations · Felix clips. Admin link at bottom for admins.
function SideRail({ mode, onMode }: { mode: Mode; onMode: (m: Mode) => void }) {
  const { isAdmin } = useAuth();
  const items: { key: Mode; title: string; icon: React.ReactNode }[] = [
    { key: "handoff", title: "Felix conversations", icon: <Phone className="w-5 h-5" /> },
    { key: "clips", title: "Felix clips", icon: <Scissors className="w-5 h-5" /> },
  ];
  return (
    <aside
      className="w-14 flex-shrink-0 flex flex-col items-center pb-3"
      style={{ background: CHROME_BG }}
    >
      <nav
        className="flex-1 w-full px-2 py-2 flex flex-col items-center gap-1.5"
        style={{ background: RAIL_BG }}
      >
        {items.map((it) => (
          <button
            key={it.key}
            title={it.title}
            onClick={() => onMode(it.key)}
            className={`relative w-10 h-10 rounded-md grid place-items-center transition ${
              mode === it.key ? "text-white bg-white/15" : "text-white/60 hover:text-white hover:bg-white/10"
            }`}
          >
            {it.icon}
          </button>
        ))}
      </nav>
      {isAdmin && (
        <div className="mt-auto w-full px-2 pb-3 flex flex-col items-center">
          <a href={buildNavUrl({ admin: "1" })} title="Admin Dashboard" className="w-10 h-10 rounded-md grid place-items-center text-white/70 hover:text-white hover:bg-white/10 transition">
            <ShieldCheck className="w-5 h-5" />
          </a>
        </div>
      )}
    </aside>
  );
}

const EMPTY_CALLS: CallLog[] = [];

// Clips longer than this get a gentle "keep it short for external sharing"
// nudge. Advisory only — never blocks saving or sharing.
const LONG_CLIP_SEC = 60;

// ── Felix clips — every saved clip across all calls, in one place. ─────────
// Shares the ["clips", "all"] key prefix with the per-call tab so a delete or
// privacy change anywhere refreshes both.
function useAllClips() {
  return useQuery({
    queryKey: ["clips", "all"],
    queryFn: async (): Promise<Clip[]> => {
      const res = await fetch("/api/clips");
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
  });
}

const REDACTION_BADGE: Record<RedactionMode, string> = {
  off: "Shows names",
  anonymize: "Anonymized",
  silence: "Silenced",
};

function ClipsListPanel({ selectedId, onSelect }: { selectedId: string | null; onSelect: (id: string) => void }) {
  const { data: clips = [], isLoading, error } = useAllClips();
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return clips;
    return clips.filter((c) =>
      [c.title, c.contact_name, c.business_name, c.transcript_excerpt, c.note, c.result, c.created_by_name]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q)));
  }, [clips, query]);

  // Keep the open clip inside the current results (first one by default).
  useEffect(() => {
    if (filtered.length > 0 && !filtered.some((c) => c.id === selectedId)) onSelect(filtered[0].id);
  }, [filtered, selectedId, onSelect]);

  return (
    <div className="h-full flex flex-col">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2 shrink-0">
        <Scissors className="w-4 h-4 text-[#F5735A]" />
        <h2 className="text-sm font-bold text-slate-900">Felix clips</h2>
        <span className="ml-auto text-xs text-slate-500">{clips.length}</span>
      </div>
      <div className="px-3 py-2 border-b border-slate-200 shrink-0">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search clips"
            className="w-full h-8 pl-8 pr-3 text-[13px] rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#F5735A]/30"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        {isLoading ? (
          <div className="p-8 grid place-items-center text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : error ? (
          <div className="px-4 py-12 text-center text-sm text-rose-600">Couldn't load clips.</div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-12 text-center text-sm text-slate-400">
            {clips.length === 0 ? "No clips yet. Open a conversation and use the scissors on the transcript to make one." : "No clips match."}
          </div>
        ) : (
          filtered.map((c) => {
            const active = c.id === selectedId;
            const mode = c.redaction_mode ?? "silence";
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => onSelect(c.id)}
                className={`w-full text-left rounded-lg border p-3 transition ${
                  active ? "border-[#F5735A] bg-[#F5735A]/5" : "border-slate-200 hover:bg-slate-50"
                }`}
              >
                <div className="flex items-start gap-2.5">
                  <div className="w-8 h-8 rounded-md bg-[#F5735A]/10 text-[#F5735A] grid place-items-center shrink-0">
                    <Play className="w-3.5 h-3.5 ml-0.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold text-slate-900 line-clamp-2">
                      {c.title || "Untitled clip"}
                    </p>
                    <p className="text-[11px] text-slate-500 truncate mt-0.5">
                      {[maskContactName(c.contact_name), c.business_name].filter(Boolean).join(" · ") || "—"}
                    </p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[10px]">
                      <span className="font-mono text-slate-500">{formatDuration(c.end_sec - c.start_sec)}</span>
                      {c.end_sec - c.start_sec > LONG_CLIP_SEC && (
                        <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">Long</span>
                      )}
                      <span className={`px-1.5 py-0.5 rounded ${mode === "off" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
                        {REDACTION_BADGE[mode]}
                      </span>
                      {c.result && (
                        <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 truncate max-w-[140px]">{c.result}</span>
                      )}
                      <span className="text-slate-400 ml-auto">
                        {new Date(c.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                      </span>
                    </div>
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

function ClipsMainView({ clipId, onOpenCall }: { clipId: string | null; onOpenCall: (callId: string) => void }) {
  const qc = useQueryClient();
  const { canCreateClips } = useAuth();
  const { data: clips = [] } = useAllClips();
  const clip = clips.find((c) => c.id === clipId) ?? null;
  const remove = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/clips/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(await res.text());
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["clips"] }),
  });

  if (!clip) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-slate-400 gap-2">
        <Scissors className="w-8 h-8 opacity-40" />
        <p className="text-sm">Select a clip</p>
      </div>
    );
  }
  return (
    <div className="h-full overflow-y-auto">
      <ClipDetailView
        clip={clip}
        canDelete={canCreateClips}
        canShare={canCreateClips}
        editableRedaction={canCreateClips}
        onModeChanged={() => qc.invalidateQueries({ queryKey: ["clips"] })}
        onDelete={() => {
          if (confirm("Delete this clip? This can't be undone.")) remove.mutate(clip.id);
        }}
      />
      <div className="px-6 pb-6">
        <button
          type="button"
          onClick={() => onOpenCall(clip.call_id)}
          className="h-8 px-3 rounded-md border border-slate-200 text-[12px] text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5"
        >
          <Phone className="w-3.5 h-3.5" /> Open full conversation
        </button>
      </div>
    </div>
  );
}

// ── Clips for a single call — rendered inside the call's Clips tab. ────────
function CallClipsTab({ callId }: { callId: string }) {
  const qc = useQueryClient();
  const { canCreateClips } = useAuth();
  const { data: clips = [], isLoading } = useQuery({
    queryKey: ["clips", callId],
    queryFn: async (): Promise<Clip[]> => {
      const res = await fetch(`/api/clips?call_id=${encodeURIComponent(callId)}`);
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/clips/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(await res.text());
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["clips"] }),
  });

  if (isLoading) {
    return (
      <div className="py-12 grid place-items-center text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }
  if (clips.length === 0) {
    return (
      <div className="py-12 text-center text-slate-500">
        <Scissors className="w-7 h-7 mx-auto mb-3 opacity-40 text-[#F5735A]" />
        <div className="text-sm font-medium text-slate-700">No clips for this call yet.</div>
        <div className="text-xs mt-1 text-slate-500">
          Switch to <span className="font-medium">Transcript</span> and click the scissors on any line to start a clip.
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      {clips.map((clip) => (
        <div key={clip.id} className="rounded-xl border border-slate-200 overflow-hidden bg-white">
          <ClipDetailView
            clip={clip}
            canDelete={canCreateClips}
            canShare={canCreateClips}
            editableRedaction={canCreateClips}
            onModeChanged={() => qc.invalidateQueries({ queryKey: ["clips"] })}
            onDelete={() => {
              if (confirm("Delete this clip? This can't be undone.")) {
                remove.mutate(clip.id);
              }
            }}
          />
        </div>
      ))}
    </div>
  );
}

// ── Clip detail view (used in both editor and viewer) ───────────────────────
export function ClipDetailView({
  clip, canDelete, onDelete, editableRedaction, onModeChanged, canShare = true,
}: {
  clip: Clip;
  canDelete: boolean;
  onDelete?: () => void;
  // When true (editor context) show the redaction-mode control so the mode can
  // be changed retrospectively. onModeChanged lets the parent refresh its cache.
  editableRedaction?: boolean;
  onModeChanged?: (clip: Clip) => void;
  // Gates the "Copy share link" button. Editor passes the allowlist result;
  // defaults true for the public board where the link is already public.
  canShare?: boolean;
}) {
  const dur = Math.max(0.1, clip.end_sec - clip.start_sec);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(clip.start_sec);
  const [copied, setCopied] = useState(false);
  const [mode, setMode] = useState<RedactionMode>(clip.redaction_mode ?? "silence");
  const [modeSaving, setModeSaving] = useState(false);
  const [audioErr, setAudioErr] = useState<string | null>(null);
  useEffect(() => { setMode(clip.redaction_mode ?? "silence"); }, [clip.id, clip.redaction_mode]);

  // Change the mode and persist it. The audio <src> carries ?m=<mode> so the
  // browser refetches the correctly-redacted render when the mode flips.
  async function changeMode(next: RedactionMode) {
    if (next === mode || modeSaving) return;
    const prev = mode;
    setMode(next); setAudioErr(null); setModeSaving(true);
    audioRef.current?.pause();
    try {
      const res = await fetch(`/api/clips/${clip.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ redaction_mode: next }),
      });
      if (!res.ok) throw new Error((await res.text()).slice(0, 200) || `${res.status}`);
      const updated = await res.json().catch(() => null);
      if (updated) onModeChanged?.(updated);
    } catch (e) {
      setMode(prev); // rollback
      setAudioErr(e instanceof Error ? e.message : "Couldn't update privacy");
    } finally {
      setModeSaving(false);
    }
  }

  // Reset on clip change.
  useEffect(() => {
    setPlaying(false);
    setCurrentTime(clip.start_sec);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = clip.start_sec;
    }
  }, [clip.id, clip.start_sec]);

  // Hook up the audio element + enforce clip bounds.
  useEffect(() => {
    const a = audioRef.current; if (!a) return;
    const onTime = () => {
      setCurrentTime(a.currentTime);
      if (a.currentTime >= clip.end_sec) {
        a.pause();
        a.currentTime = clip.end_sec;
      }
    };
    const onPlay = () => {
      // Snap forward if user resumed past the end somehow.
      if (a.currentTime < clip.start_sec || a.currentTime >= clip.end_sec) {
        a.currentTime = clip.start_sec;
      }
      setPlaying(true);
    };
    const onPause = () => setPlaying(false);
    const onEnded = () => setPlaying(false);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("play", onPlay);
    a.addEventListener("pause", onPause);
    a.addEventListener("ended", onEnded);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("play", onPlay);
      a.removeEventListener("pause", onPause);
      a.removeEventListener("ended", onEnded);
    };
  }, [clip.id, clip.start_sec, clip.end_sec]);

  const togglePlay = () => {
    const a = audioRef.current; if (!a) return;
    if (a.paused) { a.play().catch(() => { /* */ }); }
    else a.pause();
  };

  const seekFromBar = (e: React.MouseEvent<HTMLDivElement>) => {
    const a = audioRef.current; if (!a) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    a.currentTime = clip.start_sec + pct * dur;
  };

  const handleShare = async () => {
    const url = `${PUBLIC_BOARD_URL}/?clip=${clip.id}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* */ }
  };

  const elapsed = Math.max(0, currentTime - clip.start_sec);
  const pct = Math.min(100, (elapsed / dur) * 100);

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="flex-shrink-0 border-b border-slate-200 px-6 py-4 flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-9 h-9 rounded-full bg-[#F5735A]/10 text-[#F5735A] grid place-items-center shrink-0">
            <Scissors className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <h2 className="text-base font-bold text-slate-900 truncate">
              {clip.title || "Untitled clip"}
            </h2>
            <p className="text-xs text-slate-500 truncate">
              {[maskContactName(clip.contact_name), clip.business_name, formatDuration(dur)].filter(Boolean).join(" · ")}
            </p>
            {dur > LONG_CLIP_SEC && (
              <p className="mt-1 text-[11px] text-amber-700">
                Over a minute — for sharing outside Fello, a short highlight works best.
              </p>
            )}
            {clip.result && (
              <span className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                <Check className="w-3 h-3" /> {clip.result}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {canShare && (
            <button
              type="button"
              onClick={handleShare}
              className="h-8 px-3 rounded-md border border-slate-200 text-[12px] text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5"
              title="Copy share link"
            >
              {copied ? <><Check className="w-3.5 h-3.5 text-emerald-500" /> Copied</> : <><Share2 className="w-3.5 h-3.5" /> Share</>}
            </button>
          )}
          {canDelete && onDelete && (
            <button
              type="button"
              onClick={onDelete}
              className="h-8 px-2.5 rounded-md border border-rose-200 text-[12px] text-rose-600 hover:bg-rose-50 inline-flex items-center gap-1.5"
              title="Delete clip"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          )}
        </div>
      </div>

      <div className="px-6 py-5">
        <div className="rounded-lg border border-slate-200 p-5">
          <div className="flex items-center justify-center gap-4">
            <button
              type="button"
              onClick={togglePlay}
              className="w-14 h-14 rounded-full bg-[#F5735A] text-white grid place-items-center hover:opacity-90 shadow-lg"
            >
              {playing ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-0.5" />}
            </button>
          </div>
          <div className="mt-4 flex justify-between text-xs font-mono">
            <span className="text-[#F5735A] font-semibold">{formatDuration(elapsed)}</span>
            <span className="text-slate-500">{formatDuration(dur)}</span>
          </div>
          <div
            className="h-[5px] mt-1 bg-[#F5735A]/15 rounded-full cursor-pointer relative"
            onClick={seekFromBar}
          >
            <div className="absolute inset-y-0 left-0 bg-[#F5735A] rounded-full" style={{ width: `${pct}%` }} />
            <div className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full bg-white border-2 border-[#F5735A] shadow-sm" style={{ left: `calc(${pct}% - 7px)` }} />
          </div>
        </div>

        <audio
          ref={audioRef}
          src={`/api/clips/${clip.id}/audio?m=${mode}`}
          preload="metadata"
          onError={() => {
            if (mode !== "off") {
              setAudioErr("Redacted audio unavailable — this call has no transcript timing to locate names. Switch to “Show names” to play the original.");
            }
          }}
        />

        {editableRedaction && (
          <div className="mt-5">
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Privacy</label>
              {modeSaving && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
            </div>
            <RedactionModeSelect value={mode} onChange={changeMode} disabled={modeSaving} />
            <div className="mt-1.5 text-[11px] text-slate-400">Applies to this clip everywhere it's shared — change anytime.</div>
            {audioErr && <div className="mt-1.5 text-[11px] text-rose-600">{audioErr}</div>}
          </div>
        )}

        {clip.note && (
          <div className="mt-6">
            <div className="text-[11px] uppercase tracking-wide text-slate-500 font-medium mb-1.5">Note</div>
            <p className="text-sm text-slate-700 leading-relaxed">{clip.note}</p>
          </div>
        )}

        {clip.transcript_excerpt && (
          <ClipTranscriptCollapse text={clip.transcript_excerpt} />
        )}
      </div>
    </div>
  );
}

function ClipTranscriptCollapse({ text }: { text: string }) {
  // Default closed — the transcript can be long and most of the time you
  // just want to play the clip. The header still shows the wordcount so the
  // user has a sense of how much is inside before expanding.
  const [open, setOpen] = useState(false);
  const wordCount = text.trim().split(/\s+/).length;
  return (
    <div className="mt-6">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between text-left mb-2"
      >
        <div className="flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Transcript</span>
          <span className="text-[10px] text-slate-400">{wordCount} words</span>
        </div>
        <span className="inline-flex items-center gap-1 text-[12px] text-slate-500 hover:text-slate-900">
          {open ? "Hide" : "Show"}
          {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDownIcon className="w-3.5 h-3.5" />}
        </span>
      </button>
      {open && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">
          {text}
        </div>
      )}
    </div>
  );
}

// ── Clip creator modal ──────────────────────────────────────────────────────
interface ClipCreatorPayload {
  start_sec: number;
  end_sec: number;
  transcript_excerpt: string;
}

function ClipCreatorModal({
  call, payload, onClose, onCreated,
}: {
  call: CallLog;
  payload: ClipCreatorPayload;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [title, setTitle] = useState("");
  const [result, setResult] = useState("");
  const [note, setNote] = useState("");
  const [mode, setMode] = useState<RedactionMode>("silence");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [suggesting, setSuggesting] = useState(false);
  // Track whether the user has touched each field — we don't want to clobber
  // their typing if the AI response races their keystroke.
  const titleDirty = useRef(false);
  const resultDirty = useRef(false);
  const dur = payload.end_sec - payload.start_sec;

  const fetchSuggestion = useCallback(async (signal?: AbortSignal) => {
    const res = await fetch("/api/clips/suggest-meta", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ excerpt: payload.transcript_excerpt }),
      signal,
    });
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json() as Promise<{ title?: string; result?: string }>;
  }, [payload.transcript_excerpt]);

  // Auto-suggest title + result from the excerpt the moment the modal opens.
  useEffect(() => {
    if (!payload.transcript_excerpt) return;
    const ctrl = new AbortController();
    setSuggesting(true);
    fetchSuggestion(ctrl.signal)
      .then((j) => {
        if (!titleDirty.current && j?.title) setTitle(j.title);
        if (!resultDirty.current && j?.result) setResult(j.result);
      })
      .catch(() => { /* silent fallback to manual entry */ })
      .finally(() => setSuggesting(false));
    return () => ctrl.abort();
  }, [payload.transcript_excerpt, fetchSuggestion]);

  async function refreshSuggestion() {
    if (!payload.transcript_excerpt || suggesting) return;
    setSuggesting(true);
    try {
      const j = await fetchSuggestion();
      if (j?.title) { setTitle(j.title); titleDirty.current = false; }
      if (j?.result) { setResult(j.result); resultDirty.current = false; }
    } catch { /* */ }
    finally { setSuggesting(false); }
  }

  async function save() {
    setSaving(true); setErr(null);
    try {
      const res = await fetch("/api/clips", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          call_id: call.id,
          contact_name: call.contact_name,
          business_name: call.business_name,
          start_sec: payload.start_sec,
          end_sec: payload.end_sec,
          title: title.trim() || null,
          result: result.trim() || null,
          note: note.trim() || null,
          transcript_excerpt: payload.transcript_excerpt,
          redaction_mode: mode,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      onCreated();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message.slice(0, 300) : String(e));
      setSaving(false);
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm grid place-items-center p-4" onClick={() => !saving && onClose()}>
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Scissors className="w-4 h-4 text-[#F5735A]" />
            <div className="font-medium text-slate-900">Save clip</div>
          </div>
          <button type="button" onClick={onClose} disabled={saving} className="p-1.5 rounded-md hover:bg-slate-100">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div className="text-[12px] text-slate-500">
            From <span className="font-medium text-slate-900">{maskContactName(call.contact_name)}</span> ·{" "}
            <span className="font-mono">{formatDuration(payload.start_sec)} → {formatDuration(payload.end_sec)}</span>{" "}
            <span className="text-slate-400">({formatDuration(dur)})</span>
          </div>
          {dur > LONG_CLIP_SEC && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800">
              This clip is over a minute. For sharing outside Fello, a short highlight works best — avoid sharing the full call. You can still save it.
            </div>
          )}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Title (optional)</label>
              <button
                type="button"
                onClick={refreshSuggestion}
                disabled={suggesting}
                className="inline-flex items-center gap-1 text-[11px] text-[#8d6ae6] hover:text-[#7657d3] disabled:opacity-60"
                title="Regenerate title with AI"
              >
                {suggesting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                {suggesting ? "Generating…" : "Suggest with AI"}
              </button>
            </div>
            <input
              value={title}
              onChange={(e) => { titleDirty.current = true; setTitle(e.target.value); }}
              placeholder={suggesting ? "Felix is generating a title…" : "The moment they said yes"}
              className="w-full h-9 px-3 text-sm rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#F5735A]/30 focus:border-[#F5735A]/40"
              autoFocus
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wide text-slate-500 font-medium block mb-1.5">Result (optional)</label>
            <input
              value={result}
              onChange={(e) => { resultDirty.current = true; setResult(e.target.value); }}
              placeholder={suggesting ? "…and the outcome it produced" : "Resulted in a Warm Handoff"}
              className="w-full h-9 px-3 text-sm rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#F5735A]/30 focus:border-[#F5735A]/40"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wide text-slate-500 font-medium block mb-1.5">Note (optional)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="Why this clip matters"
              className="w-full px-3 py-2 text-sm rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#F5735A]/30 focus:border-[#F5735A]/40"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wide text-slate-500 font-medium block mb-1.5">Privacy</label>
            <RedactionModeSelect value={mode} onChange={setMode} disabled={saving} />
          </div>
          {payload.transcript_excerpt && (
            <div>
              <div className="text-[11px] uppercase tracking-wide text-slate-500 font-medium mb-1.5">Excerpt</div>
              <div className="text-[12px] text-slate-700 leading-relaxed max-h-40 overflow-y-auto whitespace-pre-wrap rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                {payload.transcript_excerpt}
              </div>
            </div>
          )}
          {err && <div className="text-sm text-rose-600">{err}</div>}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2 bg-slate-50">
          <button type="button" onClick={onClose} disabled={saving} className="h-8 px-3 rounded-md border border-slate-200 bg-white text-[13px] text-slate-700 hover:bg-slate-50">
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="h-8 px-3.5 rounded-md bg-[#F5735A] text-white text-[13px] font-medium hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            Save clip
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Felix conversations list (left) — internally still the "handoff" list ────
type ListFilter = "open" | "all" | "recent" | "favorites";

function HandoffListPanel({
  calls, loading, error, selectedId, onSelect, onSelectGroup,
  favorites, onToggleFavorite,
  title = "Felix conversations", icon, showChips = true,
}: {
  calls: CallLog[];
  loading: boolean;
  error: Error | null;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onSelectGroup?: (id: string) => void;
  favorites: Set<string>;
  onToggleFavorite: (id: string) => void;
  title?: string;
  icon?: React.ReactNode;
  showChips?: boolean;
}) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<ListFilter>("open");
  const [tagFilter, setTagFilter] = useState<string | null>(null);
  const [showTagDropdown, setShowTagDropdown] = useState(false);

  // Compute available tags across all calls for the dropdown
  const availableTags: ContactTag[] = [
    { label: "New Contact", color: "bg-[#F0F7FF]", textColor: "text-[#3D93F5]", reason: "Contact created within the last year" },
    { label: "Old Contact", color: "bg-[#F1F4F7]", textColor: "text-[#6B748E]", reason: "Contact created over a year ago" },
    { label: "Seller", color: "bg-[#E3FCEF]", textColor: "text-[#31A172]", reason: "Contact expressed intent to sell" },
    { label: "Buyer", color: "bg-[#F4F3FF]", textColor: "text-[#8D6AE7]", reason: "Contact expressed intent to buy" },
    { label: "Luxury", color: "bg-[#FFFAE6]", textColor: "text-[#F08400]", reason: "Property value $1M+" },
    { label: "Cash Offer", color: "bg-[#E7FDFD]", textColor: "text-[#098486]", reason: "Cash purchase/sale mentioned" },
    { label: "Expired/Sold", color: "bg-[#FFEBE6]", textColor: "text-[#F02C00]", reason: "Previously listed, under contract, or sold" },
    { label: "South", color: "bg-[#FDF1EF]", textColor: "text-[#FF725C]", reason: "Property in Southern US" },
    { label: "California", color: "bg-[#FDF1EF]", textColor: "text-[#FF725C]", reason: "Property in California" },
    { label: "Northeast", color: "bg-[#FDF1EF]", textColor: "text-[#FF725C]", reason: "Property in Northeastern US" },
    { label: "Vacation Market", color: "bg-[#FFCAD4]", textColor: "text-[#B4289A]", reason: "Property in a vacation/resort area" },
    { label: "Zillow Preferred", color: "bg-[#0C4599]", textColor: "text-white", reason: "Zillow preferred lead" },
  ];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = calls;
    if (filter === "favorites") {
      list = list.filter((c) => favorites.has(c.id));
    }
    if (filter === "recent") {
      list = [...list].sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at));
    }
    if (q) {
      list = list.filter((c) => {
        const haystack = [
          c.contact_name,
          c.business_name,
          c.contact_email,
          c.contact_phone,
          c.call_summary,
          c.current_objective_name,
          c.id,
          (c.id ?? "").toString().slice(0, 8),
          (c as unknown as Record<string, unknown>).agency_id as string | undefined,
        ].filter(Boolean).map((s) => String(s).toLowerCase());
        return haystack.some((s) => s.includes(q));
      });
    }
    // Tag filter — group calls by contact, check if the group has the tag
    if (tagFilter) {
      const grouped = groupCalls(list);
      const matchingKeys = new Set<string>();
      for (const g of grouped) {
        const tags = computeContactTags(g.calls);
        if (tags.some(t => t.label === tagFilter)) {
          matchingKeys.add(g.key);
        }
      }
      list = list.filter(c => matchingKeys.has(contactKey(c)));
    }
    return list;
  }, [calls, query, filter, favorites, tagFilter]);

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Title row */}
      <div className="flex-shrink-0 px-4 py-3 border-b border-slate-200 flex items-center gap-2">
        {icon ?? <Inbox className="w-4 h-4 text-slate-500" />}
        <h2 className="text-sm font-bold text-slate-900">{title}</h2>
        <span className="ml-auto text-xs text-slate-500">{calls.length}</span>
      </div>
      {/* Filter chips */}
      {showChips && (
      <div className="px-3 py-2.5 flex items-center gap-1.5 border-b border-slate-200 shrink-0">
        <FilterChip
          active={filter === "open"}
          onClick={() => setFilter("open")}
          icon={<span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />}
        >
          Open
        </FilterChip>
        <FilterChip
          active={filter === "favorites"}
          onClick={() => setFilter("favorites")}
          icon={<Star className={`w-3 h-3 ${filter === "favorites" ? "fill-amber-500 text-amber-500" : ""}`} />}
        >
          Favorites
          {(() => { const cnt = calls.filter(c => favorites.has(c.id)).length; return cnt > 0 ? <span className="ml-1 text-[10px] opacity-70">{cnt}</span> : null; })()}
        </FilterChip>
        <div className="relative">
          <FilterChip
            active={!!tagFilter}
            onClick={() => setShowTagDropdown(!showTagDropdown)}
          >
            {tagFilter ? tagFilter.split(" ")[0] : "Tags"}
            <ChevronDown className="w-3 h-3 ml-0.5" />
          </FilterChip>
          {showTagDropdown && (
            <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-slate-200 rounded-lg shadow-lg z-50 py-1 max-h-60 overflow-y-auto">
              <button
                onClick={() => { setTagFilter(null); setShowTagDropdown(false); }}
                className={`w-full text-left px-3 py-1.5 text-[12px] hover:bg-slate-50 ${!tagFilter ? "font-semibold text-slate-900" : "text-slate-600"}`}
              >
                All tags
              </button>
              {availableTags.map((t) => (
                <button
                  key={t.label}
                  onClick={() => { setTagFilter(t.label); setShowTagDropdown(false); }}
                  className={`w-full text-left px-3 py-1.5 text-[12px] hover:bg-slate-50 flex items-center gap-2 ${tagFilter === t.label ? "font-semibold text-slate-900" : "text-slate-600"}`}
                >
                  <span className={`inline-block w-2 h-2 rounded-full ${t.color}`} style={{ border: `1px solid ${t.textColor.replace("text-[", "").replace("]", "")}` }} />
                  {t.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      )}
      {/* Search */}
      <div className="px-3 py-2 border-b border-slate-200 shrink-0">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by contact, company, call ID…"
            className="w-full h-8 pl-8 pr-3 text-[13px] rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#F5735A]/30 focus:border-[#F5735A]/40"
          />
        </div>
      </div>
      {/* List — grouped by contact */}
      <GroupedCallList
        calls={filtered}
        selectedId={selectedId}
        onSelect={onSelect}
        onSelectGroup={onSelectGroup}
        favorites={favorites}
        onToggleFavorite={onToggleFavorite}
        loading={loading}
        error={error}
      />
    </div>
  );
}

function FilterChip({
  active, onClick, icon, children,
}: {
  active: boolean; onClick: () => void; icon?: React.ReactNode; children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-[12px] transition ${
        active
          ? "bg-[#E0F7F8] text-[#0E8A96]"
          : "bg-slate-50 text-slate-500 hover:text-slate-900 hover:bg-slate-100"
      }`}
    >
      {icon}{children}
    </button>
  );
}

// ── Grouped call list — collapses multiple calls from the same contact ────
function contactKey(c: CallLog) {
  return `${(c.contact_name || "").toLowerCase().trim()}||${(c.business_name || "").toLowerCase().trim()}||${(c.contact_phone || "").trim()}`;
}

interface CallGroup {
  key: string;
  calls: CallLog[];
  latest: CallLog;
}

function groupCalls(calls: CallLog[]): CallGroup[] {
  const map = new Map<string, CallLog[]>();
  const order: string[] = [];
  for (const c of calls) {
    const k = contactKey(c);
    if (!map.has(k)) { map.set(k, []); order.push(k); }
    map.get(k)!.push(c);
  }
  return order.map((k) => {
    const group = map.get(k)!;
    // Sort newest first within group
    group.sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at));
    return { key: k, calls: group, latest: group[0] };
  });
}

function GroupedCallList({ calls, selectedId, onSelect, onSelectGroup, favorites, onToggleFavorite, loading, error }: {
  calls: CallLog[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onSelectGroup?: (id: string) => void;
  favorites: Set<string>;
  onToggleFavorite: (id: string) => void;
  loading: boolean;
  error: Error | null;
}) {
  const groups = useMemo(() => groupCalls(calls), [calls]);

  return (
    <div className="flex-1 overflow-y-auto">
      {loading ? (
        <div className="p-8 grid place-items-center text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : error ? (
        <div className="p-4 text-[12px] text-red-600">{error.message}</div>
      ) : groups.length === 0 ? (
        <div className="px-4 py-8 text-center text-sm text-slate-400">No calls found</div>
      ) : (
        <ul className="divide-y divide-slate-200">
          {groups.map((group) => {
            const isMulti = group.calls.length > 1;
            const anySelected = group.calls.some((c) => c.id === selectedId);
            const anyFavorite = group.calls.some((c) => favorites.has(c.id));

            if (!isMulti) {
              // Single call — render as before
              return (
                <CallRow
                  key={group.latest.id}
                  call={group.latest}
                  active={selectedId === group.latest.id}
                  favorite={favorites.has(group.latest.id)}
                  onClick={() => onSelect(group.latest.id)}
                  onToggleFavorite={() => onToggleFavorite(group.latest.id)}
                />
              );
            }

            // Multi-call group — single row, opens Journey in center panel
            return (
              <li
                key={group.key}
                onClick={() => onSelectGroup?.(group.latest.id)}
                className={`group w-full text-left px-4 py-3 flex gap-3 cursor-pointer hover:bg-slate-50 transition ${
                  anySelected ? "bg-slate-100 border-l-2 border-[#F5735A]" : ""
                }`}
              >
                <div className="shrink-0">
                  <div className="w-8 h-8 rounded-full bg-[#14BACB]/15 text-[#14BACB] grid place-items-center text-xs font-semibold">
                    {initialsOf(group.latest.contact_name)}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-1 mb-1">
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#F5735A]/10 text-[#F5735A] border border-[#F5735A]/20">
                      <Sparkles className="w-2.5 h-2.5" /> Journey · {group.calls.length} calls
                    </span>
                    {group.calls.some(c => c.agent_side) && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#14BACB]/10 text-[#0E8A96] border border-[#14BACB]/30">
                        <Radio className="w-2.5 h-2.5" /> Handoff · agent side
                      </span>
                    )}
                    {group.calls.some(c => isSMS(c)) && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                        <MessageSquare className="w-2.5 h-2.5" /> SMS
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-900 truncate">
                      <MaskedContactName name={group.latest.contact_name} showToggle={false} />
                    </p>
                    {anyFavorite && <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-500 flex-shrink-0" />}
                  </div>
                  <p className="text-xs text-slate-500 line-clamp-1 mt-0.5">
                    {cleanContent(group.latest.call_summary) || group.latest.business_name || "—"}
                  </p>
                  <div className="flex items-center justify-between mt-1.5">
                    {group.latest.business_name
                      ? <span className="text-[11px] font-medium text-slate-500 truncate max-w-[160px]">{group.latest.business_name}</span>
                      : <span />}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function CallRow({
  call, active, favorite, onClick, onToggleFavorite,
}: {
  call: CallLog;
  active: boolean;
  favorite: boolean;
  onClick: () => void;
  onToggleFavorite: () => void;
}) {
  const unread = call.contact_intent === "HIGH";
  return (
    // Using a clickable <li> instead of a nested <button> inside <button> —
    // lets the star button live inside without invalid HTML.
    <li
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); }
      }}
      tabIndex={0}
      role="button"
      className={`group w-full text-left px-4 py-3 flex gap-3 cursor-pointer hover:bg-slate-50 transition outline-none focus-visible:ring-2 focus-visible:ring-[#F5735A]/40 ${
        active ? "bg-slate-100 border-l-2 border-[#F5735A]" : ""
      }`}
    >
      <div className="relative shrink-0">
        <div className="w-8 h-8 rounded-full bg-[#14BACB]/15 text-[#14BACB] grid place-items-center text-xs font-semibold">
          {initialsOf(call.contact_name)}
        </div>
        {unread && (
          <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-red-500 border-2 border-white" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        {call.agent_side && (
          <span className="mb-1 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#14BACB]/10 text-[#0E8A96] border border-[#14BACB]/30">
            <Radio className="w-2.5 h-2.5" /> Handoff · agent side
          </span>
        )}
        {isSMS(call) && (
          <span className="mb-1 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
            <MessageSquare className="w-2.5 h-2.5" /> SMS
          </span>
        )}
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-semibold text-slate-900 truncate">
            <MaskedContactName name={call.contact_name} showToggle={false} />
          </p>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggleFavorite(); }}
            title={favorite ? "Remove from favorites" : "Mark as favorite"}
            className={`w-5 h-5 rounded grid place-items-center transition flex-shrink-0 ${
              favorite
                ? "text-amber-500"
                : "text-slate-300 hover:text-amber-500 hover:bg-amber-50"
            }`}
          >
            <Star className={`w-3.5 h-3.5 ${favorite ? "fill-amber-400" : ""}`} />
          </button>
        </div>
        <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">
          {cleanContent(call.call_summary) || call.business_name || "—"}
        </p>
        <div className="flex items-center justify-between mt-1.5">
          {call.business_name ? <span className="text-[11px] font-medium text-slate-500 truncate max-w-[160px]">{call.business_name}</span> : <span />}
          <span className="text-[11px] font-mono text-slate-400 flex-shrink-0">{isSMS(call) ? `${call.total_turns ?? 0} msgs` : formatDuration(call.duration ?? 0)}</span>
        </div>
      </div>
    </li>
  );
}

// ── Search & add calls (left, in Search mode) ──────────────────────────────
// Look a call up in the DB by id / name / phone / email, preview it in the
// center, then "Add to Felix conversations". Not limited to calls with recordings —
// the point is to surface calls the default Handoff list doesn't show.
function SearchPanel({
  query, onQuery, results, loading, selectedId, onSelect, inHandoff, archivedSet,
}: {
  query: string;
  onQuery: (q: string) => void;
  results: CallLog[];
  loading: boolean;
  selectedId: string | null;
  onSelect: (id: string) => void;
  inHandoff: Set<string>;
  archivedSet: Set<string>;
}) {
  const tooShort = query.trim().length < 2;
  return (
    <div className="flex flex-col h-full bg-white">
      <div className="flex-shrink-0 px-4 py-3 border-b border-slate-200 flex items-center gap-2">
        <Search className="w-4 h-4 text-slate-500" />
        <h2 className="text-sm font-bold text-slate-900">Search calls</h2>
        {results.length > 0 && <span className="ml-auto text-xs text-slate-500">{results.length}</span>}
      </div>
      <div className="px-3 py-2.5 border-b border-slate-200 shrink-0">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <input
            value={query}
            onChange={(e) => onQuery(e.target.value)}
            placeholder="Call ID, name, phone, or email…"
            autoFocus
            className="w-full h-8 pl-8 pr-3 text-[13px] rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#F5735A]/30 focus:border-[#F5735A]/40"
          />
        </div>
        <p className="mt-1.5 text-[11px] text-slate-400">Find a call in the DB, preview it, then add it to Felix conversations.</p>
      </div>
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="p-8 grid place-items-center text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : tooShort ? (
          <div className="px-4 py-10 text-center text-sm text-slate-400">
            <Search className="w-7 h-7 mx-auto mb-3 opacity-40" />
            Type at least 2 characters to search.
          </div>
        ) : results.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-slate-400">No calls match “{query.trim()}”.</div>
        ) : (
          <ul className="divide-y divide-slate-200">
            {results.map((call) => {
              const status = inHandoff.has(call.id) ? "handoff" : archivedSet.has(call.id) ? "archived" : null;
              return (
                <li
                  key={call.id}
                  onClick={() => onSelect(call.id)}
                  tabIndex={0}
                  role="button"
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(call.id); } }}
                  className={`group w-full text-left px-4 py-3 flex gap-3 cursor-pointer hover:bg-slate-50 transition outline-none ${
                    selectedId === call.id ? "bg-slate-100 border-l-2 border-[#F5735A]" : ""
                  }`}
                >
                  <div className="w-8 h-8 shrink-0 rounded-full bg-[#14BACB]/15 text-[#14BACB] grid place-items-center text-xs font-semibold">
                    {initialsOf(call.contact_name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-slate-900 truncate">{maskContactName(call.contact_name)}</p>
                      {status === "handoff" && (
                        <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                          <Check className="w-2.5 h-2.5" /> In Handoff
                        </span>
                      )}
                      {status === "archived" && (
                        <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                          <Archive className="w-2.5 h-2.5" /> Archived
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">
                      {cleanContent(call.call_summary) || call.business_name || "—"}
                    </p>
                    <p className="text-[10px] font-mono text-slate-400 mt-1 truncate">{call.id}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

// ── Contact Journey — chat-like story across all calls with a contact ─────

function journeyOneLiner(call: CallLog): string {
  const dur = call.duration ?? 0;
  const summary = cleanContent(call.call_summary);
  if (dur === 0) return "No answer — call didn't connect.";
  if (dur < 20) return "Brief exchange — contact couldn't talk.";
  if (dur < 60 && !summary) return "Short call — light conversation.";
  if (summary) {
    // Take first sentence or first ~80 chars
    const first = summary.split(/[.!?]\s/)[0];
    return first.length > 80 ? first.slice(0, 77) + "..." : first + ".";
  }
  return "Felix followed up with the contact.";
}

function journeyDotColor(call: CallLog): string {
  const dur = call.duration ?? 0;
  if (dur === 0) return "bg-slate-300";
  if (dur < 20) return "bg-amber-400";
  if (dur < 60) return "bg-blue-400";
  return "bg-emerald-500";
}

function ContactJourney({ calls, currentCallId, onSelectCall }: { calls: CallLog[]; currentCallId: string; onSelectCall?: (id: string) => void }) {
  // AI-generated journey summary from full transcripts
  const callIds = useMemo(() => calls.map(c => c.id), [calls]);
  const { data: aiSummary, isLoading: summaryLoading } = useQuery({
    queryKey: ["journey-summary", ...callIds],
    queryFn: async (): Promise<string> => {
      const res = await fetch("/api/calls/journey-summary", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          call_ids: callIds,
          contact_name: calls[0]?.contact_name,
          business_name: calls[0]?.business_name,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      return data.summary;
    },
    staleTime: 5 * 60_000, // cache for 5 min
    retry: 1,
  });

  return (
    <div className="space-y-0">
      {calls.map((call, i) => {
        const dur = call.duration ?? 0;
        const dotColor = journeyDotColor(call);
        const oneLiner = journeyOneLiner(call);
        const isLast = i === calls.length - 1;
        const dateStr = new Date(call.created_at).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
        const timeStr = new Date(call.created_at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

        return (
          <div key={call.id} className="flex gap-4">
            {/* Timeline rail */}
            <div className="flex flex-col items-center flex-shrink-0 w-8">
              <div className={`w-3 h-3 rounded-full border-2 border-white shadow-sm ${dotColor}`} />
              {!isLast && <div className="w-px flex-1 bg-slate-200" />}
            </div>

            {/* Card — entire card is clickable */}
            <div
              onClick={() => onSelectCall?.(call.id)}
              className={`group/card flex-1 mb-4 rounded-xl p-4 border cursor-pointer transition hover:shadow-md hover:border-slate-300 hover:-translate-y-0.5 ${
                call.id === currentCallId
                  ? "bg-[#F5735A]/5 border-[#F5735A]/20"
                  : "bg-white border-slate-200"
              }`}
            >
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className="text-[13px] font-semibold text-slate-900">
                  Call attempt {i + 1}
                </span>
                <span className="text-[11px] text-slate-400">{dateStr}, {timeStr}</span>
              </div>
              <p className="text-[13px] text-slate-600 leading-relaxed">{oneLiner}</p>
              <div className="flex items-center justify-between mt-3">
                <span className="text-[11px] font-mono text-slate-400">{formatDuration(dur)}</span>
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-[#F5735A] opacity-0 group-hover/card:opacity-100 transition-opacity">
                  View details <ChevronLeft className="w-3 h-3 rotate-180" />
                </span>
              </div>
            </div>
          </div>
        );
      })}

      {/* AI Journey Summary */}
      <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-2.5">
          <div className="w-6 h-6 rounded-full bg-[#F5735A]/10 text-[#F5735A] grid place-items-center">
            <Sparkles className="w-3.5 h-3.5" />
          </div>
          <span className="text-[12px] font-semibold text-slate-900">Journey Summary</span>
        </div>
        {summaryLoading ? (
          <div className="flex items-center gap-2 text-[13px] text-slate-400">
            <Loader2 className="w-4 h-4 animate-spin" /> Writing summary from transcripts...
          </div>
        ) : aiSummary ? (
          <p className="text-[13px] text-slate-600 leading-relaxed">{aiSummary}</p>
        ) : (
          <p className="text-[13px] text-slate-400 italic">Summary unavailable.</p>
        )}
      </div>
    </div>
  );
}

// ── Call details (center) ──────────────────────────────────────────────────
type Tab = "summary" | "transcript" | "clips";

function CallDetailsView({ call, headerAction, onBackToJourney }: { call: CallLog; headerAction?: React.ReactNode; onBackToJourney?: () => void }) {
  const qc = useQueryClient();
  const { canCreateClips } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>("summary");
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(call.duration ?? 0);
  const [speed, setSpeed] = useState(1);
  const [muted, setMuted] = useState(false);

  // Clip creation state — line indexes within the rendered transcript lines.
  const [clipStart, setClipStart] = useState<number | null>(null);
  const [clipEnd, setClipEnd] = useState<number | null>(null);
  const [clipPayload, setClipPayload] = useState<ClipCreatorPayload | null>(null);

  const toggleMute = useCallback(() => {
    const a = audioRef.current; if (!a) return;
    a.muted = !a.muted;
    setMuted(a.muted);
  }, []);

  useEffect(() => {
    setPlaying(false); setCurrentTime(0);
    setDuration(call.duration ?? 0);
    setActiveTab("summary");
    setClipStart(null); setClipEnd(null); setClipPayload(null);
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
  }, [call.id, call.duration]);

  useEffect(() => {
    const a = audioRef.current; if (!a) return;
    const onTime = () => setCurrentTime(a.currentTime);
    const onMeta = () => { if (a.duration && isFinite(a.duration)) setDuration(a.duration); };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => setPlaying(false);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("play", onPlay);
    a.addEventListener("pause", onPause);
    a.addEventListener("ended", onEnded);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("play", onPlay);
      a.removeEventListener("pause", onPause);
      a.removeEventListener("ended", onEnded);
    };
  }, [call.id]);

  const seekTo = useCallback((seconds: number, autoplay = true) => {
    const a = audioRef.current; if (!a) return;
    a.currentTime = Math.max(0, Math.min(duration || a.duration || 0, seconds));
    if (autoplay && a.paused) a.play().catch(() => { /* gesture */ });
  }, [duration]);

  const togglePlay = useCallback(() => {
    const a = audioRef.current; if (!a) return;
    if (a.paused) a.play().catch(() => { /* */ });
    else a.pause();
  }, []);

  const handleSeek = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const a = audioRef.current; if (!a || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    a.currentTime = Math.max(0, Math.min(duration, (x / rect.width) * duration));
  }, [duration]);

  const skip = useCallback((seconds: number) => {
    const a = audioRef.current; if (!a) return;
    a.currentTime = Math.max(0, Math.min(duration || a.duration || 0, a.currentTime + seconds));
  }, [duration]);

  const cycleSpeed = useCallback(() => {
    const opts = [1, 1.25, 1.5, 2, 0.75];
    const next = opts[(opts.indexOf(speed) + 1) % opts.length];
    if (audioRef.current) audioRef.current.playbackRate = next;
    setSpeed(next);
  }, [speed]);

  const { data: messages = [] } = useQuery({
    queryKey: ["call-messages", call.id],
    queryFn: async (): Promise<CallMessage[]> => {
      const res = await fetch(`/api/calls/${call.id}/messages`);
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
  });

  // Word-level audio transcript (timestamped). If the table is empty or the
  // call hasn't been processed, we fall back to estimated timestamps from
  // proportional character share.
  const { data: audioWords = [] } = useQuery({
    queryKey: ["call-audio-transcript", call.id],
    queryFn: async (): Promise<AudioWord[]> => {
      const res = await fetch(`/api/calls/${call.id}/audio-transcript`);
      if (!res.ok) return [];
      return res.json();
    },
  });

  const { speakers, speakerSegments, lines } = useMemo(() => {
    // 1) Strip the noise — function-call / function-output rows are tool
    // messages the API leaks into the transcript. Drop them by role AND by
    // the [Function Call:] / [Function Output:] content prefix.
    const conversational = messages.filter((m) => {
      if (m.role === "tool") return false;
      const content = cleanContent(m.content);
      if (!content) return false;
      if (/^\[Function (Call|Output):/i.test(content)) return false;
      return true;
    });

    const dur = duration || call.duration || 0;
    const callerLabel = maskContactName(call.contact_name) || "Caller";

    // 2) Detect timestamp + speaker columns on the audio-transcript rows. We
    // don't know the schema for certain so we probe common shapes once and
    // reuse. Returns seconds for sortable comparisons.
    const tsKey = pickKey(audioWords[0], [
      "start_seconds", "start_sec", "startSec", "start_time", "startTime",
      "start_ms", "startMs", "ts_ms", "offset_ms", "timestamp_ms", "timestamp",
    ]);
    const endKey = pickKey(audioWords[0], [
      "end_seconds", "end_sec", "endSec", "end_time", "endTime",
      "end_ms", "endMs",
    ]);
    const spKey = pickKey(audioWords[0], [
      "speaker", "speaker_label", "speaker_role", "role", "source", "channel",
    ]);
    const wordKey = pickKey(audioWords[0], ["word", "text", "content", "token"]);

    const sortedWords = (audioWords ?? [])
      .map((w) => {
        const start = secOf((w as Record<string, unknown>)[tsKey ?? ""]);
        const end = secOf((w as Record<string, unknown>)[endKey ?? ""]);
        const speakerRaw = (w as Record<string, unknown>)[spKey ?? ""];
        const word = (w as Record<string, unknown>)[wordKey ?? ""];
        return {
          start: start ?? null,
          end: end ?? null,
          speaker: typeof speakerRaw === "string" ? speakerRaw : "",
          word: typeof word === "string" ? word : "",
        };
      })
      .filter((w) => w.start != null)
      .sort((a, b) => (a.start as number) - (b.start as number));

    // 3) Speaker segments — group consecutive same-speaker words into
    // contiguous spans, then express each as a % of total duration. Empty
    // arrays here cause SpeakerRow to render an empty bar (= correct: that
    // person isn't speaking during the gaps), which is exactly the
    // "no color, no one speaking" behavior the user described.
    const segments: { speaker: "felix" | "caller"; start: number; end: number }[] = [];
    if (sortedWords.length && dur > 0) {
      let cur: { sp: "felix" | "caller"; start: number; end: number } | null = null;
      for (const w of sortedWords) {
        const sp = classifySpeaker(w.speaker, callerLabel);
        if (!sp) continue;
        const start = w.start!;
        const end = w.end != null ? w.end : start + 0.3;
        if (cur && cur.sp === sp && start - cur.end < 0.6) {
          cur.end = Math.max(cur.end, end);
        } else {
          if (cur) segments.push({ speaker: cur.sp, start: cur.start, end: cur.end });
          cur = { sp, start, end };
        }
      }
      if (cur) segments.push({ speaker: cur.sp, start: cur.start, end: cur.end });
    }

    let felixSegs = segments.filter((s) => s.speaker === "felix");
    let callerSegs = segments.filter((s) => s.speaker === "caller");

    // Fallback: if the word-level transcript is missing or its columns didn't
    // probe correctly, derive segments from the conversational messages
    // themselves. We can't know exact word-by-word timing, but we can place
    // each message proportional to its character length on the timeline and
    // group consecutive same-speaker turns into spans. That's already a far
    // better visualization than empty bars + it matches the transcript order.
    if (felixSegs.length === 0 && callerSegs.length === 0 && conversational.length > 0 && dur > 0) {
      const totalChars = conversational.reduce((a, m) => a + (cleanContent(m.content).length || 1), 0) || 1;
      const fellback: { speaker: "felix" | "caller"; start: number; end: number }[] = [];
      let cursorChars = 0;
      let cur: { sp: "felix" | "caller"; start: number; end: number } | null = null;
      for (const m of conversational) {
        const len = cleanContent(m.content).length || 1;
        const startSec = (cursorChars / totalChars) * dur;
        const endSec = ((cursorChars + len) / totalChars) * dur;
        cursorChars += len;
        const sp: "felix" | "caller" = m.role === "assistant" ? "felix" : "caller";
        if (cur && cur.sp === sp) {
          cur.end = endSec;
        } else {
          if (cur) fellback.push({ speaker: cur.sp, start: cur.start, end: cur.end });
          cur = { sp, start: startSec, end: endSec };
        }
      }
      if (cur) fellback.push({ speaker: cur.sp, start: cur.start, end: cur.end });
      felixSegs = fellback.filter((s) => s.speaker === "felix");
      callerSegs = fellback.filter((s) => s.speaker === "caller");
    }

    const felixSpeakingSec = felixSegs.reduce((a, s) => a + (s.end - s.start), 0);
    const callerSpeakingSec = callerSegs.reduce((a, s) => a + (s.end - s.start), 0);

    // 4) Speaker share. Use real speaking time when we have audio words.
    // Otherwise fall back to character share of the conversational messages.
    let felixPct: number, callerPct: number;
    if (felixSpeakingSec + callerSpeakingSec > 0) {
      const t = felixSpeakingSec + callerSpeakingSec;
      felixPct = Math.round((felixSpeakingSec / t) * 100);
      callerPct = 100 - felixPct;
    } else {
      let felixChars = 0, callerChars = 0;
      for (const m of conversational) {
        const n = cleanContent(m.content).length;
        if (m.role === "assistant" || m.role === "agent") felixChars += n; else callerChars += n;
      }
      const t = felixChars + callerChars || 1;
      felixPct = Math.round((felixChars / t) * 100);
      callerPct = 100 - felixPct;
    }

    // 5) Per-message start time. If the felix_call_messages row carries a
    // timestamp directly we use it. Otherwise we project each message onto
    // the audio-word stream by walking word counts. Otherwise we fall back
    // to proportional offset (the previous behavior).
    const msgTsKey = pickKey(messages[0] ?? {}, [
      "start_seconds", "start_sec", "start_time", "start_ms", "offset_ms",
      "timestamp_ms", "timestamp", "ts", "elapsed_seconds", "elapsed_ms",
    ]);

    const wordsForMatching = sortedWords.filter((w) => w.word);

    const linesOut = conversational.map((m, i) => {
      const isAgent = m.role === "assistant" || m.role === "agent";
      const speaker = isAgent ? "Felix" : callerLabel;
      const text = cleanContent(m.content);

      let startSec: number | null = null;

      if (msgTsKey) {
        startSec = secOf((m as Record<string, unknown>)[msgTsKey]);
      }
      if (startSec == null && wordsForMatching.length > 0) {
        startSec = findMessageStartFromWords(text, isAgent, wordsForMatching, i, conversational.length);
      }
      if (startSec == null) {
        // Final fallback — proportional placement so click-to-jump at least
        // points somewhere monotonic.
        startSec = (i / Math.max(conversational.length, 1)) * dur;
      }

      return {
        speaker,
        isAgent,
        text,
        startSec: Math.max(0, Math.min(dur || startSec, startSec)),
        index: m.message_index,
      };
    });

    return {
      speakers: [
        { speaker: "Felix", percentage: felixPct },
        { speaker: callerLabel, percentage: callerPct },
      ],
      // Segments expressed as { start%, end% } so they render directly onto
      // the percentage-based bars.
      speakerSegments: {
        felix: dur > 0 ? felixSegs.map((s) => ({ start: (s.start / dur) * 100, end: (s.end / dur) * 100 })) : [],
        caller: dur > 0 ? callerSegs.map((s) => ({ start: (s.start / dur) * 100, end: (s.end / dur) * 100 })) : [],
      },
      lines: linesOut,
    };
  }, [messages, audioWords, duration, call.duration, call.contact_name]);

  const progress = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="flex-shrink-0 border-b border-slate-200">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            {onBackToJourney && (
              <button onClick={onBackToJourney} className="p-1.5 rounded-lg hover:bg-slate-100 transition text-slate-400 hover:text-slate-600" title="Back to Journey">
                <ChevronLeft className="w-4 h-4" />
              </button>
            )}
            <div className="w-8 h-8 rounded-full bg-slate-100 grid place-items-center">
              {isSMS(call) ? <MessageSquare className="w-4 h-4 text-emerald-600" /> : <Phone className="w-4 h-4 text-slate-500" />}
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                {isSMS(call) ? "SMS Conversation" : "Call Details"} <span className="text-xs font-medium text-violet-500">· Felix {isSMS(call) ? "SMS" : "Call"}</span>
                {call.agent_side && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#14BACB]/10 text-[#0E8A96] border border-[#14BACB]/30">
                    <Radio className="w-2.5 h-2.5" /> Handoff · agent side
                  </span>
                )}
              </h2>
              <p className="text-xs text-slate-500">
                Felix → {maskContactName(call.contact_name)} · {isSMS(call) ? `${call.total_turns ?? 0} messages` : formatDuration(call.duration ?? 0)}
              </p>
              {call.business_name && (
                <p className="text-xs text-slate-400 mt-0.5">{call.business_name}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {headerAction}
          </div>
        </div>

        {!isSMS(call) && (<div className="px-6 pb-4">
          <div className="rounded-lg border border-slate-200 p-5 space-y-4">
            <div className="flex items-center justify-between">
              <button onClick={cycleSpeed} className="flex items-center gap-1 text-xs font-semibold text-[#14BACB] hover:text-[#0E8A96]">
                Speed {speed}x <ChevronDown className="w-3 h-3" />
              </button>
              <div className="flex items-center gap-3">
                <button onClick={() => skip(-10)} title="Back 10s" className="w-9 h-9 rounded-full border-2 border-[#14BACB] grid place-items-center text-[#14BACB] hover:bg-[#14BACB]/5">
                  <span className="text-[10px] font-bold">10</span>
                </button>
                <button onClick={togglePlay} className="w-14 h-14 rounded-full bg-[#F5735A] text-white grid place-items-center hover:opacity-90 shadow-lg">
                  {playing
                    ? <Pause className="w-6 h-6" />
                    : currentTime >= duration && duration > 0
                      ? <RotateCcw className="w-6 h-6" />
                      : <Play className="w-6 h-6 ml-0.5" />}
                </button>
                <button onClick={() => skip(10)} title="Forward 10s" className="w-9 h-9 rounded-full border-2 border-[#14BACB] grid place-items-center text-[#14BACB] hover:bg-[#14BACB]/5">
                  <span className="text-[10px] font-bold">10</span>
                </button>
              </div>
              <button
                onClick={toggleMute}
                title={muted ? "Unmute" : "Mute"}
                className={`hover:text-slate-700 transition ${muted ? "text-rose-500" : "text-slate-400"}`}
              >
                {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </button>
            </div>

            <div className="space-y-1.5">
              <div className="flex justify-between text-xs font-mono">
                <span className="text-[#F5735A] font-semibold">{formatDuration(Math.floor(currentTime))}</span>
                <span className="text-slate-500">{formatDuration(duration)}</span>
              </div>
              <div className="h-[5px] bg-[#F5735A]/15 rounded-full cursor-pointer relative" onClick={handleSeek}>
                <div className="absolute inset-y-0 left-0 bg-[#F5735A] rounded-full" style={{ width: `${progress}%` }} />
                <div className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full bg-white border-2 border-[#F5735A] shadow-sm" style={{ left: `calc(${progress}% - 7px)` }} />
              </div>
            </div>

            <div className="space-y-4 pt-2">
              {speakers.map((s, i) => {
                const isFelix = i === 0;
                const barColor = isFelix ? "#14BACB" : "#F5735A";
                // Real speaking intervals from the word-level transcript.
                // Empty array = the speaker isn't talking during the audio →
                // empty/grey track, which is what the user described.
                const segments = isFelix ? speakerSegments.felix : speakerSegments.caller;
                return (
                  <div key={i} className="space-y-1.5">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-semibold text-slate-900">{s.speaker}</span>
                      <span className="text-sm font-semibold" style={{ color: barColor }}>{s.percentage}%</span>
                    </div>
                    <div className="relative h-[10px] rounded-full overflow-hidden bg-slate-100">
                      {/* Playhead — vertical hairline so you can see where in
                          the call you are, on top of the talk segments. */}
                      {duration > 0 && (
                        <div
                          className="absolute inset-y-0 w-px bg-slate-900/30 z-10"
                          style={{ left: `${(currentTime / duration) * 100}%` }}
                        />
                      )}
                      {segments.map((seg, j) => (
                        <div
                          key={j}
                          className="absolute inset-y-0 rounded-full"
                          style={{
                            left: `${seg.start}%`,
                            width: `${Math.max(seg.end - seg.start, 0.4)}%`,
                            backgroundColor: barColor,
                          }}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>)}

        {!isSMS(call) && (
        <div className="flex border-t border-slate-200">
          {(["summary", "transcript", "clips"] as Tab[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setActiveTab(t)}
              className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 capitalize transition ${
                activeTab === t
                  ? "text-[#F5735A] border-[#F5735A]"
                  : "text-slate-500 border-transparent hover:text-slate-700"
              }`}
            >
              {t === "summary" ? "Call Summary" : t === "transcript" ? "Transcript" : "Clips"}
            </button>
          ))}
        </div>
        )}
      </div>

      {!isSMS(call) && <audio ref={audioRef} src={`/api/calls/${call.id}/audio`} preload="metadata" />}

      <div className="flex-1 overflow-y-auto">
        {/* SMS: summary + messages merged into one view */}
        {isSMS(call) && (
          <div className="px-6 py-5 space-y-5">
            {call.call_summary && (
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-4">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Summary</p>
                <div className="space-y-1 text-[13.5px] text-slate-700 leading-relaxed">
                  {renderSummary(call.call_summary)}
                </div>
              </div>
            )}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Messages</p>
              <div className="space-y-4 max-w-3xl mx-auto">
                {lines.length === 0 ? (
                  <p className="text-sm text-slate-400 italic">No messages.</p>
                ) : lines.map((line, i) => (
                  <TranscriptBubble key={i} speaker={line.speaker} isAgent={line.isAgent} text={line.text} startSec={line.startSec} active={false} onSeek={() => {}} />
                ))}
              </div>
            </div>
          </div>
        )}
        {/* Voice calls: tabbed view */}
        {!isSMS(call) && <div className="px-6 py-5">
          {activeTab === "summary" && (
            <div className="space-y-1">
              {renderSummary(call.call_summary || "No call summary.")}
            </div>
          )}
          {activeTab === "transcript" && (
            <div className="space-y-4 max-w-3xl mx-auto">
              {lines.length === 0 ? (
                <p className="text-sm text-slate-400">No transcript available.</p>
              ) : (
                lines.map((line, i) => {
                  const active = currentTime >= line.startSec && (i === lines.length - 1 || currentTime < lines[i + 1].startSec);
                  // Clip-mode visualization. Once start is set, every other
                  // line shows a "set end" affordance; lines between start
                  // and the hovered candidate end get a subtle range tint.
                  let clipMark: "start" | "end" | "in-range" | "none" = "none";
                  if (clipStart != null) {
                    if (i === clipStart) clipMark = "start";
                    else if (clipEnd != null && i === clipEnd) clipMark = "end";
                    else if (clipEnd != null && i > Math.min(clipStart, clipEnd) && i < Math.max(clipStart, clipEnd)) clipMark = "in-range";
                  }
                  return (
                    <TranscriptBubble
                      key={line.index}
                      speaker={line.speaker}
                      isAgent={line.isAgent}
                      text={line.text}
                      startSec={line.startSec}
                      active={active}
                      onSeek={() => seekTo(line.startSec)}
                      clipMark={clipMark}
                      onMarkClip={!canCreateClips ? undefined : () => {
                        if (clipStart == null) {
                          setClipStart(i);
                        } else if (i === clipStart) {
                          // Click the start chip again to cancel.
                          setClipStart(null); setClipEnd(null);
                        } else {
                          // Second pick = end. Auto-swap if backwards so
                          // start is always earlier in the call.
                          const a = Math.min(clipStart, i);
                          const b = Math.max(clipStart, i);
                          setClipStart(a); setClipEnd(b);
                          // Build the payload + open the modal.
                          const range = lines.slice(a, b + 1);
                          const startSecVal = range[0].startSec;
                          // End = start of message AFTER the last selected
                          // (or call duration if that was the last one).
                          const endSecVal = (lines[b + 1]?.startSec) ?? (duration || call.duration || (range[range.length - 1].startSec + 6));
                          const excerpt = range.map((l) =>
                            `${l.speaker} [${formatDuration(Math.floor(l.startSec))}]:\n${l.text}`
                          ).join("\n\n");
                          setClipPayload({
                            start_sec: startSecVal,
                            end_sec: Math.max(endSecVal, startSecVal + 0.5),
                            transcript_excerpt: excerpt,
                          });
                        }
                      }}
                    />
                  );
                })
              )}
            </div>
          )}
          {activeTab === "clips" && (
            <div className="max-w-3xl mx-auto">
              <CallClipsTab callId={call.id} />
            </div>
          )}
          <p className="text-xs italic text-slate-400 mt-6">AI-generated content may be inaccurate.</p>
        </div>}
      </div>

      {/* Clip selection banner — shows once the user has marked the start. */}
      {canCreateClips && activeTab === "transcript" && clipStart != null && !clipPayload && (
        <div className="flex-shrink-0 border-t border-slate-200 bg-[#F5735A]/5 px-6 py-2.5 flex items-center gap-3">
          <Scissors className="w-4 h-4 text-[#F5735A]" />
          <div className="text-[13px] text-slate-700">
            <span className="font-medium">Clip start set</span>{" "}
            <span className="text-slate-500">— now click another line to set the end. Click the same line again to cancel.</span>
          </div>
          <div className="flex-1" />
          <button
            type="button"
            onClick={() => { setClipStart(null); setClipEnd(null); }}
            className="text-[12px] text-slate-500 hover:text-slate-900"
          >
            Cancel
          </button>
        </div>
      )}

      {canCreateClips && clipPayload && (
        <ClipCreatorModal
          call={call}
          payload={clipPayload}
          onClose={() => { setClipPayload(null); setClipStart(null); setClipEnd(null); }}
          onCreated={() => {
            setClipPayload(null); setClipStart(null); setClipEnd(null);
            qc.invalidateQueries({ queryKey: ["clips", call.id] });
            setActiveTab("clips");
          }}
        />
      )}
    </div>
  );
}

// ── Transcript bubble — chat style, agent right, caller left ───────────────
function TranscriptBubble({
  speaker, isAgent, text, startSec, active, onSeek,
  clipMark = "none", onMarkClip,
}: {
  speaker: string;
  isAgent: boolean;
  text: string;
  startSec: number;
  active: boolean;
  onSeek: () => void;
  clipMark?: "start" | "end" | "in-range" | "none";
  onMarkClip?: () => void;
}) {
  // Pick the row tint based on clip selection state — start/end stand out
  // with a coral border, in-range gets a soft tint, otherwise active-playhead
  // tint, otherwise hover-only.
  const rowTint =
    clipMark === "start" ? "bg-[#F5735A]/10 ring-1 ring-[#F5735A]/40"
    : clipMark === "end" ? "bg-[#F5735A]/10 ring-1 ring-[#F5735A]/40"
    : clipMark === "in-range" ? "bg-[#F5735A]/5"
    : active ? "bg-[#F5735A]/5"
    : "hover:bg-slate-50";
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSeek}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSeek(); } }}
      title="Click to jump to this point in the recording"
      className={`group relative w-full flex items-start gap-2 px-2 py-1.5 -mx-2 rounded transition cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-[#F5735A]/40 ${rowTint}`}
    >
      {/* Avatar + bubble live in a sub-row so we can flex-reverse them for
          Felix turns while keeping the scissors gutter anchored to the right
          of the OUTER row — consistent regardless of speaker side. */}
      <div className={`flex-1 min-w-0 flex gap-2.5 ${isAgent ? "flex-row-reverse" : ""}`}>
      <div
        className="w-7 h-7 rounded-full grid place-items-center shrink-0 text-[10px] font-semibold mt-1"
        style={{
          background: isAgent ? "#E0F7F8" : "#F1F5F9",
          color: isAgent ? "#0E8A96" : "#475569",
        }}
      >
        {initialsOf(speaker)}
      </div>
      <div className={`flex-1 min-w-0 ${isAgent ? "text-right" : "text-left"}`}>
        <div className={`text-[11px] mb-1 flex items-center gap-1.5 ${isAgent ? "justify-end" : ""}`}>
          {isAgent ? (
            <>
              <span className={`font-mono ${active ? "text-[#F5735A]" : "text-slate-400"}`}>
                {formatDuration(Math.floor(startSec))}
              </span>
              <span className={`font-semibold ${active ? "text-[#F5735A]" : "text-slate-700"}`}>{speaker}</span>
            </>
          ) : (
            <>
              <span className={`font-semibold ${active ? "text-[#F5735A]" : "text-slate-700"}`}>{speaker}</span>
              <span className={`font-mono ${active ? "text-[#F5735A]" : "text-slate-400"}`}>
                {formatDuration(Math.floor(startSec))}
              </span>
            </>
          )}
        </div>
        <div
          className={`inline-block max-w-[85%] px-3.5 py-2 rounded-2xl text-[13.5px] leading-snug whitespace-pre-wrap text-left ${
            isAgent
              ? "bg-violet-50 border border-violet-100 text-slate-900"
              : "bg-slate-100 text-slate-900"
          }`}
        >
          {text}
        </div>
      </div>
      </div>
      {/* Scissors clip button — always visible, always on the FAR RIGHT of
          the row regardless of which side the speaker bubble sits on. Stops
          propagation so it doesn't double-fire the bubble's seek. */}
      {onMarkClip && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onMarkClip(); }}
          title={
            clipMark === "start" ? "Clip start — click again to cancel"
            : clipMark === "end" ? "Clip end"
            : clipMark === "in-range" ? "Inside selected clip range"
            : "Mark this as the clip start (or end)"
          }
          className={`shrink-0 mt-1 w-7 h-7 rounded grid place-items-center transition ${
            clipMark === "start" || clipMark === "end"
              ? "text-[#F5735A] bg-[#F5735A]/10"
              : "text-slate-400 hover:text-[#F5735A] hover:bg-[#F5735A]/5"
          }`}
        >
          <Scissors className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

// ── Contact details (right column, matches image 23) ───────────────────────
function ContactDetailsPanel({ call, siblingCalls }: { call: CallLog; siblingCalls?: CallLog[] }) {
  const property = call.property_json ?? null;
  const transfer = call.transfer_attempts?.[0];
  const address = readPath(property, ["address"]) as string | null;
  const avm = readPath(property, ["valuation", "estimatedValue"]) as number | string | null;
  const equity = readPath(property, ["equity", "estimatedEquity"]) as number | string | null;
  const tags = useMemo(() => computeContactTags(siblingCalls && siblingCalls.length > 0 ? siblingCalls : [call]), [call, siblingCalls]);

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="h-[52px] px-5 flex items-center justify-between border-b border-slate-200 shrink-0">
        <div className="text-[14px] font-medium text-slate-900">Contact Details</div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {/* Contact header card */}
        <div className="px-5 py-4 border-b border-slate-200">
          <h3 className="text-[19px] font-semibold leading-tight"><MaskedContactName name={call.contact_name} /></h3>
          {tags.length > 0 && (
            <div className="mt-3">
              <ContactTagChips tags={tags} />
            </div>
          )}
        </div>

        {/* Signals */}
        <Section
          title="Signals"
          rightBadge={call.contact_intent === "HIGH" ? { text: "High Intent", tone: "danger" } : undefined}
        >
          {call.current_objective_name && (
            <SignalRow>
              Contact actively pursuing <span className="font-medium">{call.current_objective_name.toLowerCase()}</span>
            </SignalRow>
          )}
          {call.enrollment_reason && (
            <SignalRow>{truncate(call.enrollment_reason, 110)}</SignalRow>
          )}
          {transfer?.status === "COMPLETED" && (
            <SignalRow>
              Successful agent handoff
            </SignalRow>
          )}
          {!call.current_objective_name && !call.enrollment_reason && transfer?.status !== "COMPLETED" && (
            <div className="text-[12px] text-slate-400">No signals recorded.</div>
          )}
        </Section>

        {/* Lead Stage */}
        <Section title="Lead Stage">
          <div className="text-[12px] mb-1 flex items-center gap-1.5">
            <span className="text-slate-500">Stage</span>
            <Info className="w-3 h-3 text-slate-400" />
          </div>
          <div className="text-[14px] font-medium text-slate-900">
            {call.contact_intent === "HIGH" ? "Qualified" : "New"}
          </div>
          <div className="mb-3" />
          <div className="flex items-center gap-1.5">
            <StageChip active={call.contact_intent !== "HIGH"}>New</StageChip>
            <StageChip>Follow-Up</StageChip>
            <StageChip active={call.contact_intent === "HIGH"}>Qualified</StageChip>
          </div>
        </Section>

        {/* Felix AI */}
        <Section title="Felix AI">
          <div className="rounded-xl border border-[#8d6ae6]/30 bg-gradient-to-br from-[#faf6ff] via-white to-[#fff5fa] p-3">
            <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-[#8d6ae6] font-medium mb-1.5">
              <Sparkles className="w-3 h-3" /> Felix AI
            </div>
            <p className="text-[12px] text-slate-700 leading-snug">
              {call.call_summary || "No summary yet."}
            </p>
            <div className="mt-3 pt-2.5 border-t border-[#8d6ae6]/15 space-y-1.5 text-[11px]">
              <div className="flex justify-between items-center">
                <span className="text-slate-700">Nurture</span>
                <span className="inline-flex items-center gap-1 text-emerald-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Enrolled
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-700">Sales Agent</span>
                <span className="inline-flex items-center gap-1 text-rose-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-rose-500" /> Excluded
                </span>
              </div>
            </div>
          </div>
        </Section>

        {/* Properties */}
        {address && (
          <Section title="Properties">
            <PropertyCard
              label="Current Owner"
              address={address}
              avm={avm}
              equity={equity}
            />
          </Section>
        )}

        {/* Details (collapsed by default) */}
        <Section title="Details" defaultCollapsed>
          <div className="space-y-1.5 text-[12px]">
            {call.call_type && <DetailRow label="Type">{call.call_type}</DetailRow>}
            <DetailRow label="Duration">{formatDuration(call.duration ?? 0)}</DetailRow>
            {call.total_turns != null && <DetailRow label="Turns">{String(call.total_turns)}</DetailRow>}
            <DetailRow label="When">{new Date(call.created_at).toLocaleString()}</DetailRow>
            <DetailRow label="Call ID">
              <span className="font-mono text-[11px]">{call.id.slice(0, 8)}…</span>
            </DetailRow>
          </div>
        </Section>
      </div>
    </div>
  );
}


function StageChip({ active, children }: { active?: boolean; children: React.ReactNode }) {
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
      active ? "bg-[#F5735A] text-white" : "bg-slate-100 text-slate-500"
    }`}>
      {children}
    </span>
  );
}

function Section({
  title, rightBadge, defaultCollapsed = false, children,
}: {
  title: string;
  rightBadge?: { text: string; tone: "danger" | "info" | "success" };
  defaultCollapsed?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(!defaultCollapsed);
  return (
    <div className="border-b border-slate-200">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full px-5 py-3 flex items-center justify-between"
      >
        <div className="flex items-center gap-2">
          <span className="text-[13px] font-medium text-slate-900">{title}</span>
          {rightBadge && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${
              rightBadge.tone === "danger" ? "bg-rose-50 text-rose-700" :
              rightBadge.tone === "success" ? "bg-emerald-50 text-emerald-700" :
              "bg-sky-50 text-sky-700"
            }`}>
              {rightBadge.text}
            </span>
          )}
        </div>
        <ChevronUp className={`w-3.5 h-3.5 text-slate-400 transition-transform ${open ? "" : "rotate-180"}`} />
      </button>
      {open && <div className="px-5 pb-4">{children}</div>}
    </div>
  );
}

function SignalRow({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 text-[12px] text-slate-700 py-1.5">
      <Radio className="w-3 h-3 text-[#8d6ae6] mt-0.5 shrink-0" />
      <div className="flex-1">{children}</div>
    </div>
  );
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-slate-500 w-20 shrink-0">{label}</span>
      <span className="flex-1">{children}</span>
    </div>
  );
}

function PropertyCard({
  label, address, avm, equity,
}: {
  label: string;
  address: string;
  avm: number | string | null;
  equity: number | string | null;
}) {
  const parts = address.split(",").map((s) => s.trim());
  const line1 = parts[0] ?? address;
  const line2 = parts.slice(1).join(", ");
  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-3 py-1.5 border-b border-slate-200 text-[11px] text-slate-500 flex items-center gap-1.5">
        <span className="w-3 h-3 rounded-sm bg-slate-300" /> {label}
      </div>
      <div className="p-3 flex items-start gap-3">
        <div className="w-12 h-12 rounded bg-gradient-to-br from-slate-200 to-slate-300 grid place-items-center text-slate-400 shrink-0">
          <Phone className="w-4 h-4 opacity-50" />
        </div>
        <div className="flex-1 min-w-0 text-[12px]">
          <div className="font-medium text-slate-900 truncate">{line1}</div>
          {line2 && <div className="text-slate-500 truncate">{line2}</div>}
          {(avm != null || equity != null) && (
            <div className="text-slate-500 mt-1">
              {avm != null && <span>AVM {formatMoney(avm)}</span>}
              {avm != null && equity != null && <span> · </span>}
              {equity != null && <span>{formatMoney(equity)} equity</span>}
            </div>
          )}
        </div>
        <button className="text-slate-400 hover:text-slate-600">
          <MoreVertical className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// ── Summary renderer ────────────────────────────────────────────────────────
function renderSummary(text: string): React.ReactNode[] {
  return text.split("\n").map((line, i) => {
    if (!line.trim()) return <div key={i} className="h-3" />;
    const bold = /^\*\*(.+)\*\*:?$/.exec(line.trim());
    if (bold) return <p key={i} className="font-semibold text-slate-900 mt-3 mb-1">{bold[1]}</p>;
    if (line.trim().startsWith("•") || line.trim().startsWith("-")) {
      return (
        <li key={i} className="ml-4 text-sm text-slate-600 list-disc">
          {line.trim().replace(/^[•-]\s*/, "")}
        </li>
      );
    }
    return <p key={i} className="text-sm text-slate-600 leading-relaxed">{line}</p>;
  });
}

// ── Audio-transcript helpers (defensive about column names) ────────────────
function pickKey(row: unknown, candidates: string[]): string | null {
  if (!row || typeof row !== "object") return null;
  const r = row as Record<string, unknown>;
  for (const k of candidates) if (k in r && r[k] != null) return k;
  return null;
}

// Convert whatever the row stores (seconds vs ms, number vs numeric string)
// into seconds. Treat values > 600 as milliseconds — no Felix call is 10+
// minutes _measured in seconds_ in our corpus, so this is a safe heuristic.
function secOf(raw: unknown): number | null {
  if (raw == null) return null;
  const n = typeof raw === "string" ? parseFloat(raw) : (raw as number);
  if (!isFinite(n)) return null;
  return n > 600 ? n / 1000 : n;
}

function classifySpeaker(label: string, callerName: string): "felix" | "caller" | null {
  if (!label) return null;
  const lo = label.toLowerCase();
  if (lo.includes("felix") || lo.includes("agent") || lo.includes("assistant") || lo === "ai" || lo === "bot" || lo === "0") return "felix";
  if (lo.includes(callerName.toLowerCase().split(" ")[0] || "___")) return "caller";
  if (lo === "user" || lo === "human" || lo === "customer" || lo === "caller" || lo === "1") return "caller";
  // Unknown labels usually correspond to the contact (sole "other" speaker).
  return "caller";
}

// Walk the word stream once per render, advancing by message length so each
// message lands on the timestamp of its first word. This is approximate but
// vastly more accurate than pure character-proportional placement.
function findMessageStartFromWords(
  text: string,
  _isAgent: boolean,
  words: { start: number | null; speaker: string; word: string }[],
  messageIdx: number,
  totalMessages: number,
): number | null {
  if (!words.length) return null;
  // Estimate the per-message word stride and snap.
  const msgWordCount = Math.max(1, text.trim().split(/\s+/).length);
  // Pull a cumulative word index from the message index — assumes roughly
  // even pacing of words across the conversation. Good enough for jump-to.
  const totalWords = words.length;
  const wordsPerMessage = totalWords / Math.max(totalMessages, 1);
  const targetWordIdx = Math.min(totalWords - 1, Math.floor(messageIdx * wordsPerMessage));
  const w = words[targetWordIdx];
  if (!w || w.start == null) return null;
  // Nudge backward if the snapped word is mid-sentence — find the previous
  // word boundary by stepping back up to (msgWordCount/2) words.
  const back = Math.min(targetWordIdx, Math.floor(msgWordCount / 2));
  const candidate = words[targetWordIdx - back] ?? w;
  return candidate.start;
}

// Felix's TTS pipeline emits SSML-style pause hints (e.g. <break time="0.5s" />)
// inside the message content so the speech synthesizer paces itself naturally.
// They're meaningless to a human reader, so strip them everywhere we surface
// transcript text. Same for any other angle-bracket SSML tag that slipped in.
function cleanContent(raw: string | null | undefined): string {
  if (!raw) return "";
  return raw
    .replace(/<\s*break\b[^>]*\/?\s*>/gi, "")
    .replace(/<\/?\s*(emphasis|prosody|speak|say-as|voice|phoneme|sub|s|p|w|mark)\b[^>]*>/gi, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function initialsOf(name: string | null | undefined): string {
  if (!name) return "?";
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join("");
}

function formatDuration(seconds: number | null | undefined): string {
  if (!seconds || !isFinite(seconds)) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function formatMoney(n: number | string | null | undefined): string {
  const num = typeof n === "string" ? parseFloat(n) : n;
  if (num == null || !isFinite(num)) return "—";
  return num.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

function readPath(obj: unknown, path: string[]): unknown {
  let cur: unknown = obj;
  for (const p of path) {
    if (cur && typeof cur === "object" && p in (cur as Record<string, unknown>)) {
      cur = (cur as Record<string, unknown>)[p];
    } else return null;
  }
  return cur ?? null;
}

// Reserve unused imports for future iterations without TS warnings.
void Plus;
